import { Location } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewChecked, AfterViewInit, ChangeDetectorRef, EventEmitter, Output, TemplateRef, HostListener } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AiService } from 'src/app/services/ai.service';
import { ToastService } from 'src/app/services/toast.service';
import { AlertService } from 'src/app/services/alert.service';
import { WhisperService } from 'src/app/services/whisper.service';
import { LayoutService } from 'src/app/components/layout/layout.service';
import { FloatingThreadPresentation, FloatingThreadService, FloatingThreadState } from 'src/app/services/floating-thread.service';
import { ThemeService } from 'src/app/services/theme.service';
import { AgentCatalogCacheService } from 'src/app/services/agent-catalog-cache.service';
import { CatalogCacheResult } from 'src/app/services/model-catalog-cache.service';
import { AgentBackendTargetService } from 'src/app/services/agent-backend-target.service';
import { AttachedContextService } from './attached-context.service';
import { PlaybookSubmission } from './input-container/prompt-popover/prompt-popover.component';
import { PlaybookEntry } from 'src/app/data/playbook-catalog';
import { ChatMessagesAreaComponent } from './chat-messages-area/chat-messages-area.component';
import { marked } from 'marked';
import { Subscription, combineLatest, firstValueFrom, timeout } from 'rxjs';
import { AgentContextAttachment, AgentImageAttachment, AgentModelsResponse, AgentTodoItem, ForceCompactionResponse, OpenRouterModelSummary, OpenRouterProviderRecommendation, ResponsePreferences, SystemPromptLayerSet, ToolFilterConfig, ToolInfo } from '@platfio/interfaces/platfio';
import { 
  AgentOption, 
  ChatDisplayMode,
  ChatDeviceCommandTarget,
  ChatThreadAssetItem,
  ChatFileAttachment,
  ChatImageAttachment,
  ChatMessageUpdateEvent,
  ChatPreviewKind,
  ChatPreviewOutlet,
  ChatPreviewPresentation,
  ChatThreadViewAction,
  ChatThreadViewOption,
  ModeOption, 
  ModelOption,
  ToolOption,
  ContextItem,
  AttachedContextItem,
  ShortcutDemonstration, 
  InputChangeEvent, 
  ThreadActionEvent, 
  ChatKeyboardEvent,
  TransformedChatMessage
} from './interfaces/chat-component.interfaces';
import { formatModelDisplayName, getCanonicalModelKey } from './utilities/token-usage.utils';
import { InputContainerComponent } from './input-container/input-container.component';
import { agentSelectorOptions, defaultAgentSlug, getAgentName, getAgentSystemPrompt } from 'src/app/data/agent-catalog';
import { getToolNamesForGroups, inferToolSelectionMode, ToolSelectionMode } from 'src/app/data/agent-tool-defaults';
import { getLegacyToolCatalogMessage, getToolCatalogCompatibilityMetadata, isLegacyToolCatalogResponse } from 'src/app/data/tool-catalog-compat';
import { UiModalService } from 'src/app/components/elements/ui-modal/ui-modal.service';
import { environment } from 'src/environments/environment';

type ModelCatalogStatus = 'loading' | 'live' | 'error';
type ToolCatalogStatus = 'loading' | 'live' | 'error';

interface CreditTopUpOption {
  credits: number;
  priceUsd: number;
}

const SUPPORTED_IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const MAX_IMAGE_ATTACHMENTS = 4;
const MAX_IMAGE_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const MAX_CONTEXT_FILE_BYTES = 25 * 1024 * 1024;

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  host: { '[class.theme-light]': 'theme.isLight' }
})
export class ChatComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked, AfterViewInit {

  private readonly chatBackendPreferenceKey = 'chatBackendPreference';
  private readonly chatDisplayModePreferenceKey = 'chatDisplayModePreference';
  private readonly chatRenderPreviewsInlinePreferenceKey = 'chatRenderPreviewsInlinePreference';
  private readonly defaultThreadStatus = 'waiting_for_user';
  private readonly appApi = environment.useEmulator
    ? `http://127.0.0.1:5001/${environment.firebaseConfig.projectId}/us-central1/app`
    : `https://us-central1-${environment.firebaseConfig.projectId}.cloudfunctions.net/app`;
  private readonly terminalThreadStatuses = new Set(['waiting_for_user', 'complete', 'stopped', 'error']);
  private sendRequestInFlight = false;
  private loadingSpecificThreadId: string | null = null;
  private loadingSpecificThreadPromise: Promise<void> | null = null;
  private specificThreadAuthRetryCounts = new Map<string, number>();
  private readonly maxSpecificThreadAuthRetries = 20;

  temperature = 0.9;
  topK = 16;
  topP = 0.1;
  maxOutput = 5000;

  // Backend integration properties
  currentThreadId: string | null = null;
  availableThreads: any[] = [];
  history: any[] = []; // Raw messages from database - no transformation

  // Development environment detection and backend URL management
  isLocalDevelopment = false;
  useLocalBackend = false;
  
  get backendUrl(): string {
    return this.agentBackendTargetService.getTarget(this.useLocalBackend).url;
  }

  get backendEnvironment(): 'production' | 'staging' {
    return this.agentBackendTargetService.getTarget(this.useLocalBackend).environment;
  }

  get hostedBackendLabel(): string {
    return this.agentBackendTargetService.getEnvironmentLabel(this.agentBackendTargetService.getCloudEnvironment());
  }

  newMessage: string = '';
  response: string = '';
  pendingSystemPromptOverride: string | null = null;
  pendingPlaybookId: string | null = null;

  recognizing: boolean = false;
  recognition: any;

  @Input() context: string = ''
  @Input() agency: string = ''
  @Input() agent: string = defaultAgentSlug
  @Input() contextUid?: string // UID for the context (business, agency, affiliate, etc.)
  @Input() contextType?: 'user' | 'agency' | 'business' | 'affiliate' // Type of context for labeling
  @Input() threadId?: string // Specific thread ID to load and display
  @Input() inFloatingPanel?: boolean = false // Whether this chat is displayed in a floating panel
  @Input() useLocalBackendOverride?: boolean // Explicit backend override for pages that surface backend controls outside chat
  @Input() autofocusOnLoad?: boolean = false // Whether to autofocus the input on component load
  @Input() displayMode: ChatDisplayMode = 'standard'
  @Input() renderPreviewsInline = false
  @Input() requestedDetachedPreviewKind: ChatPreviewKind | null = null
  @Input() requestedDetachedPreviewId: string | null = null
  @Input() previewPresentation: ChatPreviewPresentation = 'modal'
  @Input() fullPageLayout: boolean = false
  @Input() fullPageContentMaxWidth: number = 1080
  @Input() fullPageContentPaddingStart: number = 28
  @Input() fullPageContentPaddingEnd: number = 28
  @Input() fullPageContentMobilePaddingStart: number = 16
  @Input() fullPageContentMobilePaddingEnd: number = 16
  @Input() footerBottomPadding: number = 8
  @Input() footerInlinePadding: number = 28
  @Input() footerMobileInlinePadding: number = 16
  @Input() footerShellPaddingStart: number = 10
  @Input() footerShellPaddingEnd: number = 10
  @Input() footerShellMobilePaddingStart: number = 16
  @Input() footerShellMobilePaddingEnd: number = 16

  @Output() previewOutletChange = new EventEmitter<ChatPreviewOutlet | null>();

  @ViewChild('chatContainer') chatContainer?: ElementRef<HTMLElement>;
  @ViewChild('settingsButton') settingsButton!: ElementRef;
  @ViewChild('chatMessages') chatMessages!: ChatMessagesAreaComponent;
  @ViewChild('scrollableContent') scrollableContent!: ElementRef;
  @ViewChild('scrollableContentInner') scrollableContentInner?: ElementRef<HTMLElement>;
  @ViewChild('inputWrapper') inputWrapper?: ElementRef<HTMLElement>;
  @ViewChild(InputContainerComponent) inputContainer!: InputContainerComponent;
  @ViewChild('threadDeleteProgressTemplate') threadDeleteProgressTemplate?: TemplateRef<any>;
  @ViewChild('addCreditsModalBody') addCreditsModalBody?: TemplateRef<any>;
  @ViewChild('addCreditsModalFooter') addCreditsModalFooter?: TemplateRef<any>;

  userObject: any;
  agencyObject: any;
  currentUser: any;
  contextUserObject: any; // User/business object for the context UID

  loading: boolean = false;
  isLoadingThread: boolean = false;
  initializingThread: boolean = false; // For immediate feedback when creating a new thread
  showOptions: boolean = false;
  editingThreadId: string | null = null;
  mode: 'ask' | 'agent' = 'agent';
  selectedModel?: ModelOption;
  selectedAudioModelKey: string = '';
  selectedImageModelKey: string = '';
  selectedVideoModelKey: string = '';
  selectedAudioVoice: string = '';
  availableModels: ModelOption[] = [];
  availableVideoModels: ModelOption[] = [];
  availableTools: ToolOption[] = [];
  selectedToolNames: string[] = [];
  defaultToolNames: string[] = [];
  private toolSelectionMode: ToolSelectionMode = 'all';
  private hasManualToolSelection = false;
  private pendingPlaybookToolGroupKeys: string[] = [];
  defaultAudioModelKey: string = '';
  defaultAudioVoice: string = '';
  defaultImageModelKey: string = '';
  defaultVideoModelKey: string = '';
  audioVoiceOptionsByModel: Record<string, Array<{ value: string; label: string }>> = {};
  providerRecommendations: OpenRouterProviderRecommendation[] = [];
  modelCatalogStatus: ModelCatalogStatus = 'loading';
  modelCatalogSourceLabel: string = 'Loading model catalog';
  modelCatalogMessage: string = 'Requesting the live model catalog from the selected backend.';
  toolCatalogStatus: ToolCatalogStatus = 'loading';
  toolCatalogSourceLabel: string = 'Loading tool catalog';
  toolCatalogMessage: string = 'Requesting the live tool catalog from the selected backend.';
  // Agent selector popover
  showAgentPopover: boolean = false;
  initialScrollPending: boolean = false;
  chatHistoryLoaded: boolean = false;

  // Prompt functionality
  showPromptPopover: boolean = false;
  promptCursorPosition: number = 0;
  selectedPromptIndex: number = 0;

  // Agent selector functionality
  showAgentSelectorPopover: boolean = false;
  agentCursorPosition: number = 0;
  selectedAgentIndex: number = 0;

  // Context reference functionality (# symbol)
  showContextPopover: boolean = false;
  contextCursorPosition: number = 0;
  selectedContextIndex: number = 0;
  contextBusinessName: string = '';
  availablePlans: any[] = [];
  availableNotes: any[] = [];
  availableProposals: any[] = [];
  availableTasks: any[] = [];
  availableTickets: any[] = [];
  availableApps: any[] = [];
  availableMedia: any[] = [];
  contextItems: any[] = []; // Combined context items for display
  private contextItemSubscriptions: Subscription[] = [];

  // Enhanced settings popover (+ symbol)
  showModePopover: boolean = false;
  selectedModeIndex: number = 1; // Default to Agent mode (index 1)
  maxIterations: number = 50; // Default max iterations for agent mode
  modeCursorPosition: number = 0;
  modeOptions: { value: 'ask' | 'agent', label: string, type: 'mode' }[] = [
    { value: 'ask', label: 'Ask', type: 'mode' },
    { value: 'agent', label: 'Agent', type: 'mode' }
  ];

  // Thread details popover (? symbol)
  showThreadDetailsPopover: boolean = false;
  threadDetailsCursorPosition: number = 0;
  private commandPopoverSource: 'keyboard' | 'ui' = 'keyboard';

  // Audio recording state
  isExpanded: boolean = false; // For expandable input
  isRecording: boolean = false;
  isConnecting: boolean = false; // New connecting state for immediate feedback
  isProcessingTranscription: boolean = false;
  transcriptionStartTime: number = 0;
  showTranscriptionCancel: boolean = false;
  private transcriptionSubscription: Subscription | null = null;
  private recordingSubscription: Subscription | null = null;
  private processingSubscription: Subscription | null = null;
  private rightPanelSubscription: Subscription | null = null;

  // Drag and drop state
  isDragOver: boolean = false;
  private dragCounter: number = 0;
  private chatSubscription: Subscription | null = null;
  private threadSubscription: Subscription | null = null; // Add thread document subscription
  private focusChatInputSubscription: Subscription | null = null;
  private authStateSubscription: Subscription | null = null;
  private userObjectSubscription: Subscription | null = null;
  private agencyObjectSubscription: Subscription | null = null;
  private creditBalanceSubscription: Subscription | null = null;
  private threadsListSubscription: Subscription | null = null;
  private contextUserSubscription: Subscription | null = null;
  private transcriptionTimeoutId: any = null;
  private shouldFocusOnNextCheck: boolean = false;

  // Available agents for the dropdown
  availableAgents: AgentOption[] = agentSelectorOptions;

  // Attached context
  attachedContextItems: AttachedContextItem[] = [];
  private attachedContextSubscription: Subscription | null = null;

  readonly creditTopUpOptions: CreditTopUpOption[] = [
    { credits: 10000, priceUsd: 10 },
    { credits: 50000, priceUsd: 50 },
    { credits: 100000, priceUsd: 100 }
  ];
  selectedCreditTopUpCredits = 10000;
  creditBalanceAgencyId: string | null = null;
  creditBalanceLoading = false;
  remainingCredits: number | null = null;
  allocatedCredits = 0;
  usedCredits = 0;
  isLoadingCreditTopUpCards = false;
  isAddingCredits = false;
  creditTopUpCardCount = 0;
  creditTopUpCardLabel = '';
  creditTopUpError = '';
  attachedImageItems: ChatImageAttachment[] = [];
  attachedFileItems: ChatFileAttachment[] = [];
  threadAssetItems: ChatThreadAssetItem[] = [];
  isDeletingThread: boolean = false;
  threadDeletionStatusLabel: string = '';
  threadDeletionStatusDetail: string = '';
  threadDeletionProcessedCount: number = 0;
  threadDeletionTotalCount: number = 0;
  forceCompactionLoading: boolean = false;
  private activePreviewOutlet: ChatPreviewOutlet | null = null;
  threadTodoPopoverTop = 24;
  threadTodoPopoverRight = 32;
  threadTodoPopoverWidth = 260;
  threadTodoPopoverDefaultExpanded = true;
  threadTodoPopoverHasRoom = true;
  private threadTodoPopoverResizeObserver?: ResizeObserver;

  readonly handleImageFilesSelected = (files: File[]): void => {
    console.log('[chat-attachments] direct image file handler invoked', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });
    this.onImageFilesSelected(files);
  };

  readonly handleFilesSelected = (files: File[]): void => {
    console.log('[chat-attachments] direct generic file handler invoked', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });
    this.onFilesSelected(files);
  };

  constructor(public ai: AiService,
              public afAuth: AngularFireAuth,
              public toastService: ToastService,
              public afs: AngularFirestore,
              public afStorage: AngularFireStorage,
              public whisperService: WhisperService,
              public layoutService: LayoutService,
              private http: HttpClient,
              private router: Router,
              private location: Location,
              private alertService: AlertService,
              private floatingThreadService: FloatingThreadService,
              private attachedContextService: AttachedContextService,
              public theme: ThemeService,
              private cdr: ChangeDetectorRef,
              private agentCatalogCache: AgentCatalogCacheService,
              private agentBackendTargetService: AgentBackendTargetService,
              private uiModalService: UiModalService
  ) { 
    this.isLocalDevelopment = this.agentBackendTargetService.isLocalHostname();
    this.useLocalBackend = this.getInitialBackendPreference();

    // Configure marked options for better rendering
    marked.setOptions({
      breaks: true,
      gfm: true
    });
  }

  // Get the effective UID to use for thread operations
  get effectiveUid(): string {
    return this.contextUid || this.currentUser?.uid || '';
  }

  get effectiveAgencyId(): string {
    return this.resolveAgencyId();
  }

  get hasNoAgencyCredits(): boolean {
    return !!this.creditBalanceAgencyId
      && !this.creditBalanceLoading
      && typeof this.remainingCredits === 'number'
      && this.remainingCredits <= 0;
  }

  get selectedCreditTopUpOption(): CreditTopUpOption {
    return this.creditTopUpOptions.find(option => option.credits === this.selectedCreditTopUpCredits)
      || this.creditTopUpOptions[0];
  }

  get canAddSelectedCredits(): boolean {
    return !!this.creditBalanceAgencyId
      && this.creditTopUpCardCount > 0
      && !this.isLoadingCreditTopUpCards
      && !this.isAddingCredits;
  }

  private resolveAgencyId(): string {
    if (this.agency) {
      return this.agency;
    }

    if (this.currentThread?.agencyId) {
      return this.currentThread.agencyId;
    }

    if (this.userObject?.agency) {
      return this.userObject.agency;
    }

    if (this.userObject?.agent) {
      return this.userObject.agent;
    }

    if (this.userObject?.axipAgency === true && this.currentUser?.uid) {
      return this.currentUser.uid;
    }

    return '';
  }

  private syncCreditBalanceSubscription(): void {
    const agencyId = this.resolveAgencyId();
    if (agencyId === this.creditBalanceAgencyId && this.creditBalanceSubscription) {
      return;
    }

    this.creditBalanceSubscription?.unsubscribe();
    this.creditBalanceSubscription = null;
    this.creditBalanceAgencyId = agencyId || null;
    this.remainingCredits = null;
    this.allocatedCredits = 0;
    this.usedCredits = 0;

    if (!agencyId) {
      this.creditBalanceLoading = false;
      return;
    }

    this.creditBalanceLoading = true;
    const monthKey = this.buildMonthKey(new Date());
    this.creditBalanceSubscription = combineLatest([
      this.afs.doc<any>(`users/${agencyId}/creditAllocationMonthlySummaries/${monthKey}`).valueChanges(),
      this.afs.doc<any>(`users/${agencyId}/creditUsageMonthlySummaries/${monthKey}`).valueChanges()
    ]).subscribe({
      next: ([allocationSummary, usageSummary]) => {
        this.allocatedCredits = this.roundCredits(Number(allocationSummary?.totalCredits || 0));
        this.usedCredits = this.roundCredits(Number(usageSummary?.totalCredits || 0));
        this.remainingCredits = this.roundCredits(this.allocatedCredits - this.usedCredits);
        this.creditBalanceLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading agency credit balance:', error);
        this.creditBalanceLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  openAddCreditsModal(): void {
    if (!this.addCreditsModalBody) {
      return;
    }

    this.selectedCreditTopUpCredits = this.creditTopUpOptions[0].credits;
    this.creditTopUpError = '';
    void this.loadCreditTopUpCards();

    this.uiModalService.open({
      title: 'Add credits',
      size: 'sm',
      bodyTemplate: this.addCreditsModalBody,
      footerRightTemplate: this.addCreditsModalFooter,
      bodyClass: 'credit-top-up-modal-body',
      closeOnBackdropClick: true,
      hideFooter: false
    });
  }

  selectCreditTopUp(option: CreditTopUpOption): void {
    this.selectedCreditTopUpCredits = option.credits;
    if (this.creditTopUpCardCount > 0) {
      this.creditTopUpError = '';
    }
  }

  async addSelectedCredits(): Promise<void> {
    const agencyId = this.creditBalanceAgencyId || this.resolveAgencyId();
    const option = this.selectedCreditTopUpOption;
    if (!agencyId || !this.canAddSelectedCredits) {
      return;
    }

    this.isAddingCredits = true;
    this.creditTopUpError = '';

    try {
      await firstValueFrom(this.http.post(`${this.appApi}/credits/top-up`, {
        agencyId,
        credits: option.credits
      }).pipe(timeout(20000)));

      this.allocatedCredits = this.roundCredits(this.allocatedCredits + option.credits);
      this.remainingCredits = this.roundCredits((this.remainingCredits || 0) + option.credits);
      this.toastService.showSuccess(`Added ${this.formatCredits(option.credits)} credits`);
      this.uiModalService.close();
    } catch (error) {
      console.error('Failed to add credits:', error);
      this.creditTopUpError = this.getCreditTopUpErrorMessage(error);
    } finally {
      this.isAddingCredits = false;
      this.cdr.markForCheck();
    }
  }

  async loadCreditTopUpCards(): Promise<void> {
    const agencyId = this.creditBalanceAgencyId || this.resolveAgencyId();
    this.creditTopUpCardCount = 0;
    this.creditTopUpCardLabel = '';

    if (!agencyId) {
      this.creditTopUpError = 'Unable to resolve the agency account for this chat.';
      return;
    }

    this.isLoadingCreditTopUpCards = true;
    try {
      const customer = await firstValueFrom(this.http.post<any>(`${this.appApi}/customer`, { uid: agencyId }));
      const sources = Array.isArray(customer?.sources?.data) ? customer.sources.data : [];
      this.creditTopUpCardCount = sources.length;
      const defaultSource = sources.find((source: any) => source?.id === customer?.default_source) || sources[0];
      if (defaultSource) {
        const brand = defaultSource?.card?.brand || defaultSource?.brand || 'Card';
        const last4 = defaultSource?.card?.last4 || defaultSource?.last4 || '';
        this.creditTopUpCardLabel = last4 ? `${brand} ending ${last4}` : brand;
      }

      if (!sources.length) {
        this.creditTopUpError = 'Add a payment card to the agency account before adding credits.';
      }
    } catch (error) {
      this.creditTopUpCardCount = 0;
      this.creditTopUpError = 'Add a payment card to the agency account before adding credits.';
    } finally {
      this.isLoadingCreditTopUpCards = false;
      this.cdr.markForCheck();
    }
  }

  goToAgencyCards(): void {
    const agencyId = this.creditBalanceAgencyId || this.resolveAgencyId();
    this.uiModalService.close();
    void this.router.navigate(['/myagency/settings'], {
      queryParams: {
        uid: agencyId,
        tab: 'payments',
        paymentMode: 'cards',
        agencyId
      }
    });
  }

  goToAgencyCredits(): void {
    const agencyId = this.creditBalanceAgencyId || this.resolveAgencyId();
    this.uiModalService.close();
    void this.router.navigate(['/myagency/settings'], {
      queryParams: {
        uid: agencyId,
        agencyId,
        tab: 'credits'
      }
    });
  }

  goToAgencyPricing(): void {
    const agencyId = this.creditBalanceAgencyId || this.resolveAgencyId();
    this.uiModalService.close();
    void this.router.navigate(['/help-center'], {
      queryParams: {
        tab: 'pricing',
        type: 'agency',
        uid: agencyId,
        agencyId
      }
    });
  }

  formatCredits(value: unknown): string {
    const numeric = Number(value || 0);
    return new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: numeric >= 100 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(numeric);
  }

  private getCreditTopUpErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return 'Unable to add credits right now.';
    }

    const candidate = error as {
      message?: string;
      error?: { message?: string } | string;
    };
    if (typeof candidate.error === 'string') {
      return candidate.error;
    }

    if (candidate.error && typeof candidate.error === 'object' && typeof candidate.error.message === 'string') {
      if (candidate.error.message.includes("Unexpected token '<'")) {
        return 'Unable to reach the credit top-up function. Deploy the latest backend functions and try again.';
      }
      return candidate.error.message;
    }

    if (candidate.message?.includes('Http failure during parsing')) {
      return 'Unable to reach the credit top-up function. Deploy the latest backend functions and try again.';
    }

    return String(candidate.message || 'Unable to add credits right now.');
  }

  private buildMonthKey(date: Date): string {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
  }

  private roundCredits(value: number): number {
    return Number(value.toFixed(3));
  }

  private getThreadAgent(thread: any): string {
    return thread?.agentName || thread?.agentType || this.agent;
  }

  private async persistCurrentThreadAgent(agentName: string): Promise<void> {
    if (!this.currentThreadId) {
      return;
    }

    const updatedThread = this.currentThread
      ? {
          ...this.currentThread,
          agentName,
          agentType: agentName,
          updatedAt: new Date()
        }
      : null;

    if (updatedThread) {
      const threadIndex = this.availableThreads.findIndex(thread => thread.id === this.currentThreadId);
      if (threadIndex !== -1) {
        this.availableThreads[threadIndex] = updatedThread;
        this._cachedThread = updatedThread;
        this._cachedThreadId = updatedThread.id;
        this._cachedThreadsLength = this.availableThreads.length;
      }
    }

    try {
      await this.afs.collection('threads').doc(this.currentThreadId).update({
        agentName,
        agentType: agentName,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating thread agent:', error);
    }
  }

  private buildSystemPromptLayersForRequest(): Partial<SystemPromptLayerSet> {
    const layers: Partial<SystemPromptLayerSet> = {
      agent: getAgentSystemPrompt(this.agent)
    };

    if (this.pendingSystemPromptOverride !== null) {
      layers.custom = this.pendingSystemPromptOverride || '';
    }

    return layers;
  }

  private normalizePreferenceString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private normalizeToolNames(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const seen = new Set<string>();

    return value
      .map(item => typeof item === 'string' ? item.trim() : '')
      .filter(name => {
        if (!name || seen.has(name)) {
          return false;
        }

        seen.add(name);
        return true;
      });
  }

  private getThreadResponsePreferences(thread: any): Partial<ResponsePreferences> | undefined {
    return thread?.executionConfig?.responsePreferences;
  }

  private getThreadPreferredModelKey(thread: any): string | undefined {
    return thread?.executionConfig?.preferredModel || thread?.model || thread?.lastUsedModel;
  }

  private getThreadSelectedToolNames(thread: any): string[] {
    const toolPolicy = thread?.executionConfig?.toolPolicy;

    if (!toolPolicy || toolPolicy.mode === 'all') {
      return [];
    }

    if (toolPolicy.mode === 'allow' && Array.isArray(toolPolicy.include)) {
      return this.normalizeToolNames(toolPolicy.include);
    }

    return [];
  }

  private hydrateMediaPreferencesFromThread(thread: any): void {
    const responsePreferences = this.getThreadResponsePreferences(thread);
    this.selectedAudioModelKey = this.normalizePreferenceString(responsePreferences?.audio?.model);
    this.selectedImageModelKey = this.normalizePreferenceString(responsePreferences?.image?.model);
    this.selectedVideoModelKey = this.normalizePreferenceString(responsePreferences?.video?.model);
    this.selectedAudioVoice = this.normalizePreferenceString(responsePreferences?.audio?.voice);
  }

  private hydrateToolSelectionFromThread(thread: any): void {
    this.selectedToolNames = this.getThreadSelectedToolNames(thread);
    this.refreshDefaultToolNames();
    this.toolSelectionMode = inferToolSelectionMode(this.selectedToolNames, this.defaultToolNames);
    this.hasManualToolSelection = this.toolSelectionMode === 'custom';
  }

  private buildToolFilterForRequest(): ToolFilterConfig {
    if (this.selectedToolNames.length === 0) {
      return { mode: 'all' };
    }

    return {
      mode: 'allow',
      include: [...this.selectedToolNames]
    };
  }

  private buildResponsePreferencesForRequest(): Partial<ResponsePreferences> | undefined {
    const existingPreferences = this.getThreadResponsePreferences(this.currentThread);
    const hasAudioPreferencePayload = !!this.selectedAudioModelKey || !!this.selectedAudioVoice || !!existingPreferences?.audio?.model || !!existingPreferences?.audio?.voice;
    const hasImagePreferencePayload = !!this.selectedImageModelKey || !!existingPreferences?.image?.model;
    const hasVideoPreferencePayload = !!this.selectedVideoModelKey || !!existingPreferences?.video?.model;

    const responsePreferences: Partial<ResponsePreferences> = {};

    if (hasAudioPreferencePayload) {
      responsePreferences.audio = {
        model: this.selectedAudioModelKey,
        voice: this.selectedAudioVoice
      };
    }

    if (hasImagePreferencePayload) {
      responsePreferences.image = {
        model: this.selectedImageModelKey
      };
    }

    if (hasVideoPreferencePayload) {
      responsePreferences.video = {
        model: this.selectedVideoModelKey
      };
    }

    return Object.keys(responsePreferences).length > 0 ? responsePreferences : undefined;
  }

  private normalizeThread(thread: any, id?: string): any {
    if (!thread) {
      return thread;
    }

    return {
      ...thread,
      ...(id ? { id } : {}),
      agencyId: thread.agencyId || thread.agency,
      agency: thread.agency || thread.agencyId,
      agentName: thread.agentName || thread.agentType,
      agentType: thread.agentType || thread.agentName
    };
  }

  private areSuggestedFollowUpsEqual(previousValue: unknown, nextValue: unknown): boolean {
    const previous = Array.isArray(previousValue) ? previousValue : [];
    const next = Array.isArray(nextValue) ? nextValue : [];

    if (previous.length !== next.length) {
      return false;
    }

    return previous.every((item, index) => item === next[index]);
  }

  private areMessageSnapshotsEqual(previousMessage: any, nextMessage: any): boolean {
    if (!previousMessage || !nextMessage) {
      return previousMessage === nextMessage;
    }

    return previousMessage.id === nextMessage.id
      && this.areComparableValuesEqual(previousMessage.content, nextMessage.content)
      && this.areComparableValuesEqual(previousMessage.message?.content, nextMessage.message?.content)
      && this.areSuggestedFollowUpsEqual(previousMessage.suggestedFollowUps, nextMessage.suggestedFollowUps)
      && this.areSuggestedFollowUpsEqual(previousMessage.message?.suggestedFollowUps, nextMessage.message?.suggestedFollowUps);
  }

  private areComparableValuesEqual(previousValue: unknown, nextValue: unknown): boolean {
    if (previousValue === nextValue) {
      return true;
    }

    return JSON.stringify(previousValue ?? null) === JSON.stringify(nextValue ?? null);
  }

  private buildThreadData(): any {
    const agencyId = this.resolveAgencyId();
    const playbookId = this.normalizePreferenceString(this.pendingPlaybookId);

    if (!agencyId) {
      throw new Error('No agency context available');
    }

    return {
      uid: agencyId,
      agencyId,
      agency: agencyId,
      agentName: this.agent,
      agentType: this.agent,
      ...(playbookId ? { playbookId } : {}),
      mode: this.mode,
      model: this.selectedModel?.key || null,
      executionConfig: {
        mode: this.mode,
        ...(this.selectedModel?.key ? { preferredModel: this.selectedModel.key } : {}),
        toolPolicy: this.buildToolFilterForRequest()
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      status: this.defaultThreadStatus,
      contextType: 'agency',
      archived: false
    };
  }

  /**
   * Check if we're in specific thread mode (threadId provided as input)
   * When true, hide thread selection UI since user is locked to this thread
   */
  get isSpecificThreadMode(): boolean {
    return !!this.threadId;
  }

  get composerSuggestionsPlacement(): 'above' | 'hidden' {
    if (this.history.length > 0) {
      return 'hidden';
    }

    if ((this.isSpecificThreadMode || !!this.currentThreadId) && !this.chatHistoryLoaded) {
      return 'hidden';
    }

    return 'above';
  }

  /**
   * Get the current thread object from availableThreads (cached)
   */
  private _cachedThread: any | null = null;
  private _cachedThreadId: string | null = null;
  private _cachedThreadsLength = 0;

  get currentThread(): any | null {
    if (!this.currentThreadId || !this.availableThreads.length) {
      this._cachedThread = null;
      this._cachedThreadId = null;
      this._cachedThreadsLength = 0;
      return null;
    }
    // Only re-find if the thread ID or the array length changed
    if (this._cachedThreadId !== this.currentThreadId || this._cachedThreadsLength !== this.availableThreads.length) {
      this._cachedThreadId = this.currentThreadId;
      this._cachedThreadsLength = this.availableThreads.length;
      this._cachedThread = this.availableThreads.find(thread => thread.id === this.currentThreadId) || null;
    }
    return this._cachedThread;
  }

  get shouldShowThreadTodoPopover(): boolean {
    const items = this.currentThread?.todoList?.items;
    return this.displayMode !== 'minimal'
      && this.currentThreadId !== null
      && this.threadTodoPopoverHasRoom
      && Array.isArray(items)
      && items.some((item: any) => !!item?.id && !!item?.title)
      && !this.hasSidecarPreviewOpen;
  }

  private get hasSidecarPreviewOpen(): boolean {
    return this.previewPresentation === 'sidecar' && !!this.activePreviewOutlet;
  }

  get viewOptions(): ChatThreadViewOption[] {
    const hasThread = !!this.currentThreadId;
    const currentView = this.getCurrentThreadView();
    const canPin = this.canUsePinnedView();

    return [
      {
        id: 'page',
        label: 'Page',
        description: 'Open this thread on its own page.',
        icon: 'fa-solid fa-up-right-from-square',
        active: currentView === 'page',
        disabled: !hasThread || currentView === 'page'
      },
      {
        id: 'floating',
        label: 'Floating',
        description: 'Keep it in a movable window.',
        icon: 'fa-regular fa-window-restore',
        active: currentView === 'floating',
        disabled: !hasThread || currentView === 'floating'
      },
      {
        id: 'pinned',
        label: 'Pinned',
        description: canPin ? 'Dock it to the side of the workspace.' : 'Pinned mode is only available on larger screens.',
        icon: 'fa-solid fa-thumbtack',
        active: currentView === 'pinned',
        disabled: !hasThread || !canPin || currentView === 'pinned'
      },
      {
        id: 'fullscreen',
        label: 'Fullscreen',
        description: 'Expand it into a large overlay.',
        icon: 'fa-solid fa-expand',
        active: currentView === 'fullscreen',
        disabled: !hasThread || currentView === 'fullscreen'
      },
      {
        id: 'minimize',
        label: 'Minimize',
        description: 'Hide it until you need it again.',
        icon: 'fa-solid fa-window-minimize',
        disabled: !hasThread
      }
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['useLocalBackendOverride'] && typeof this.useLocalBackendOverride === 'boolean') {
      this.useLocalBackend = this.isLocalDevelopment ? this.useLocalBackendOverride : false;
      this.loadAvailableModels(this.selectedModel?.key);
      this.loadAvailableTools(this.selectedToolNames);
    }

    // Handle specific thread ID input
    if (changes['threadId'] && this.threadId) {
      this.loadSpecificThread(this.threadId);
    }
    
    // Reload threads when contextUid changes
    if (changes['contextUid'] && !changes['contextUid'].firstChange) {
      // Only clear and reload if no specific threadId is provided
      if (!this.threadId) {
        this.clearSelectedThread();
        this.loadExistingThreads();
      }
      this.loadContextUser();
    }
    
    // Load plans, notes, and credit balance when agency changes
    if (changes['agency']) {
      if (!changes['agency'].firstChange) {
        this.loadPlansAndNotes();
      }
      this.syncCreditBalanceSubscription();
    }
  }

  // Load the user/business object for the context UID
  loadContextUser(): void {
    this.contextUserSubscription?.unsubscribe();
    if (this.contextUid) {
      this.contextUserSubscription = this.afs.doc(`users/${this.contextUid}`).valueChanges().subscribe(contextUser => {
        this.contextUserObject = contextUser;
      });
    } else {
      this.contextUserObject = null;
    }
  }

  ngOnInit(): void {
    this.renderPreviewsInline = this.getInitialRenderPreviewsInlinePreference();

    // Subscribe to attached context changes
    this.attachedContextSubscription = this.attachedContextService.attachedItems$.subscribe(items => {
      this.attachedContextItems = items;
      void this.persistAttachedContext();
      this.cdr.detectChanges();
    });

    // Set up global drag event listeners for better drag detection
    this.setupGlobalDragListeners();

    // Load existing threads when user is available
    this.authStateSubscription = this.afAuth.authState.subscribe(user => {
      this.currentUser = user;
      if (user) {
        // Clean up previous inner subscriptions on each auth state change
        this.userObjectSubscription?.unsubscribe();
        this.agencyObjectSubscription?.unsubscribe();

        this.userObjectSubscription = this.afs.doc(`users/${user.uid}`).valueChanges().subscribe(userObject => {
          this.userObject = userObject;
          this.syncCreditBalanceSubscription();
          // Once we have the user object (needed by resolveAgencyId), load threads if not in specific-thread mode
          if (!this.threadId) {
            this.loadExistingThreads();
          }
        });

        if (this.agency) {
          this.agencyObjectSubscription = this.afs.doc('users/' + this.agency).valueChanges().subscribe((agencyObject) => {
            this.agencyObject = agencyObject;
            this.syncCreditBalanceSubscription();
          });
        }

        // If a specific threadId is provided, load that thread
        if (this.threadId) {
          // Only reload if we haven't already loaded this thread
          if (this.currentThreadId !== this.threadId) {
            this.loadSpecificThread(this.threadId);
          }
        }
        this.loadContextUser(); // Load context user on init
      }
    });

    // If a specific threadId is provided, try to load immediately
    // (don't wait for auth — the thread doc is fetchable)
    if (this.threadId && !this.currentThreadId) {
      this.loadSpecificThread(this.threadId);
    }

    this.loadAvailableModels();
    this.loadAvailableTools();

    // Load plans and notes for the current agency
    this.loadPlansAndNotes();

    // Watch for contextUid changes and reload threads
    // Since Angular doesn't have a built-in way to watch @Input changes in ngOnInit,
    // we'll handle this in ngOnChanges instead

    // Subscribe to transcription updates
    this.transcriptionSubscription = this.whisperService.transcription$.subscribe(transcription => {
      if (transcription && transcription.trim()) {
        this.newMessage = transcription;
        
        // Expand to textarea and focus input after transcription
        this.isExpanded = true;
        setTimeout(() => {
          this.focusInput();
        }, 0);
        
        this.transcriptionSubscription?.unsubscribe();
      }
    });

    // Subscribe to recording state changes
    this.recordingSubscription = this.whisperService.isRecording$.subscribe(isRecording => {
      this.isRecording = isRecording;
    });

    // Subscribe to processing state changes
    this.processingSubscription = this.whisperService.isProcessingTranscription$.subscribe((isProcessing: boolean) => {
      this.isProcessingTranscription = isProcessing;
      
      if (isProcessing) {
        // Start timing when transcription begins
        this.transcriptionStartTime = Date.now();
        this.showTranscriptionCancel = false;
        
        // Set timer to show cancel button after 10 seconds
        this.transcriptionTimeoutId = setTimeout(() => {
          if (this.isProcessingTranscription) {
            this.showTranscriptionCancel = true;
          }
        }, 10000);
      } else {
        // Clear timing when transcription ends
        this.showTranscriptionCancel = false;
        if (this.transcriptionTimeoutId) {
          clearTimeout(this.transcriptionTimeoutId);
          this.transcriptionTimeoutId = null;
        }
      }
    });

    // Subscribe to right panel toggle for auto-focus
    this.rightPanelSubscription = this.layoutService.toggleRightPanel$.subscribe(() => {
      // Check if the panel is opening (was closed, now open)
      if (this.layoutService.rightOpen) {
        this.shouldFocusOnNextCheck = true;
        
        // Also try immediate focus with delay as fallback
        setTimeout(() => {
          if (this.layoutService.rightOpen) {
            // this.focusInput();
          }
        }, 300);
      } else {
        this.shouldFocusOnNextCheck = false;
      }
    });

    // Subscribe to focus chat input keyboard shortcut
    this.focusChatInputSubscription = this.layoutService.focusChatInput$.subscribe(() => {
      // Only focus if right panel is open
      if (this.layoutService.rightOpen) {
        this.focusInput();
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up global drag listeners
    this.cleanupGlobalDragListeners();

    // Clean up subscriptions
    if (this.transcriptionSubscription) {
      this.transcriptionSubscription.unsubscribe();
    }
    if (this.recordingSubscription) {
      this.recordingSubscription.unsubscribe();
    }
    if (this.processingSubscription) {
      this.processingSubscription.unsubscribe();
    }
    if (this.rightPanelSubscription) {
      this.rightPanelSubscription.unsubscribe();
    }
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
    }
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
    }
    if (this.focusChatInputSubscription) {
      this.focusChatInputSubscription.unsubscribe();
    }
    if (this.attachedContextSubscription) {
      this.attachedContextSubscription.unsubscribe();
    }
    this.authStateSubscription?.unsubscribe();
    this.userObjectSubscription?.unsubscribe();
    this.agencyObjectSubscription?.unsubscribe();
    this.creditBalanceSubscription?.unsubscribe();
    this.threadsListSubscription?.unsubscribe();
    this.contextUserSubscription?.unsubscribe();
    this.threadTodoPopoverResizeObserver?.disconnect();
    this.contextItemSubscriptions.forEach(s => s.unsubscribe());
    
    // Clean up transcription timeout
    if (this.transcriptionTimeoutId) {
      clearTimeout(this.transcriptionTimeoutId);
      this.transcriptionTimeoutId = null;
    }
    
    // Stop recording if still active
    if (this.isRecording) {
      this.whisperService.stopRecording();
    }
    
    // Clear connecting state
    this.isConnecting = false;
    this.clearAttachedImages();
    this.previewOutletChange.emit(null);
  }

  onPreviewOutletChanged(previewOutlet: ChatPreviewOutlet | null): void {
    this.activePreviewOutlet = previewOutlet;
    this.previewOutletChange.emit(previewOutlet);
  }

  onThreadAssetsChanged(assetItems: ChatThreadAssetItem[]): void {
    this.threadAssetItems = assetItems;
  }

  onThreadAssetSelected(assetItem: ChatThreadAssetItem): void {
    this.chatMessages?.openThreadAsset(assetItem);
  }

  onDeviceCommandRequested(target: ChatDeviceCommandTarget): void {
    this.inputContainer?.openDevicesAuditModal(target);
  }

  ngAfterViewInit(): void {
    this.setupThreadTodoPopoverObserver();
    setTimeout(() => this.updateThreadTodoPopoverLayout(), 0);

    // Autofocus the input if the autofocusOnLoad property is true
    if (this.autofocusOnLoad) {
      // Use setTimeout to ensure the view is fully initialized
      setTimeout(() => {
        this.focusInput();
      }, 100);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateThreadTodoPopoverLayout();
  }

  private setupThreadTodoPopoverObserver(): void {
    if (typeof ResizeObserver === 'undefined' || !this.chatContainer?.nativeElement) {
      return;
    }

    this.threadTodoPopoverResizeObserver?.disconnect();
    this.threadTodoPopoverResizeObserver = new ResizeObserver(() => {
      this.updateThreadTodoPopoverLayout();
    });
    this.threadTodoPopoverResizeObserver.observe(this.chatContainer.nativeElement);
  }

  private updateThreadTodoPopoverLayout(): void {
    if (
      typeof window === 'undefined'
      || !this.chatContainer?.nativeElement
      || !this.scrollableContent?.nativeElement
      || !this.scrollableContentInner?.nativeElement
    ) {
      return;
    }

    const panelRect = this.chatContainer.nativeElement.getBoundingClientRect();
    const scrollRect = this.scrollableContent.nativeElement.getBoundingClientRect();
    const innerRect = this.scrollableContentInner.nativeElement.getBoundingClientRect();
    const scrollbarWidth = Math.max(
      0,
      this.scrollableContent.nativeElement.offsetWidth - this.scrollableContent.nativeElement.clientWidth
    );
    const edgeInset = 16;
    const gutterGap = panelRect.width < 640 ? 10 : 16;
    const rightEdge = scrollRect.right - scrollbarWidth - edgeInset;
    const leftEdge = innerRect.right + gutterGap;
    const availableGutterWidth = Math.max(0, rightEdge - leftEdge);
    const maxWidth = panelRect.width >= 1500 ? 360 : panelRect.width >= 1100 ? 300 : 260;
    const minVisibleWidth = 112;
    const width = Math.floor(Math.min(maxWidth, availableGutterWidth));

    this.threadTodoPopoverHasRoom = width >= minVisibleWidth;
    this.threadTodoPopoverTop = Math.max(0, Math.round(scrollRect.top - panelRect.top));
    this.threadTodoPopoverRight = Math.max(8, Math.round(panelRect.right - rightEdge));
    this.threadTodoPopoverWidth = Math.max(minVisibleWidth, width);
    this.threadTodoPopoverDefaultExpanded = width >= 220 && scrollRect.height >= 420;
  }

  private applyPendingThreadDraft(threadId: string): void {
    const pendingDraft = this.floatingThreadService.consumeThreadDraft(threadId);
    if (!pendingDraft) {
      return;
    }

    this.newMessage = pendingDraft.message;
    this.pendingSystemPromptOverride = pendingDraft.systemPromptOverride || null;
    this.attachedImageItems = Array.isArray(pendingDraft.imageAttachments)
      ? [...pendingDraft.imageAttachments]
      : [];
    this.attachedFileItems = Array.isArray(pendingDraft.fileAttachments)
      ? [...pendingDraft.fileAttachments]
      : [];
    this.isExpanded = pendingDraft.message.includes('\n') || pendingDraft.message.length > 140;

    setTimeout(() => {
      this.focusInput();
      // Auto-send the draft message
      void this.sendMessage();
    }, 0);
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusOnNextCheck && this.layoutService.rightOpen) {
      this.shouldFocusOnNextCheck = false;
      this.focusInput();
    }
  }

  returnAiAssName() {
    return `${getAgentName(this.agent, '')} `;
  }

  private markdownCache = new Map<string, string>();

  parseMarkdown = (text: string | undefined): string => {
    const input = text || '';
    const cached = this.markdownCache.get(input);
    if (cached !== undefined) {
      return cached;
    }
    try {
      const result = marked(input) as string;
      this.markdownCache.set(input, result);
      // Cap cache size to prevent unbounded growth
      if (this.markdownCache.size > 500) {
        const firstKey = this.markdownCache.keys().next().value;
        if (firstKey !== undefined) this.markdownCache.delete(firstKey);
      }
      return result;
    } catch (error) {
      return input;
    }
  }

  getRoleDisplayName(role: string): string {
    switch (role) {
      case 'user':
        return 'You';
      case 'assistant':
      case 'model':
        return 'Assistant';
      case 'system':
        return 'System';
      case 'tool':
        return 'Tool';
      default:
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
  }

  formatToolCalls(toolCalls: any[]): string {
    if (!toolCalls || toolCalls.length === 0) return '';
    
    return toolCalls.map(call => {
      let args = '';
      try {
        const parsedArgs = JSON.parse(call.function.arguments);
        args = Object.entries(parsedArgs)
          .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
          .join(', ');
      } catch {
        args = call.function.arguments;
      }
      
      return `🔧 **${call.function.name}**(${args})`;
    }).join('\n\n');
  }

  focusInput(): void {
    if (this.inputContainer && !this.isProcessingTranscription && !this.loading) {
      this.inputContainer.focusInput();
    }
  }

  refresh() {
    this.loading = true;
    this.isConnecting = false; // Reset connecting state on refresh
    setTimeout(() => {
      this.createNewThread();
      this.loading = false;
    }, 1000)
  }

  resetAndCloseOptions() {
    // Close options panel
    this.showOptions = false;
    // Then reset the session
    this.refresh();
  }

  toggleOptions() {
    this.showOptions = !this.showOptions;
  }

  toggleMode() {
    this.mode = this.mode === 'ask' ? 'agent' : 'ask';
    this.toastService.showSuccess(`Switched to ${this.mode.toUpperCase()} mode`);
  }

  toggleBackend(): void {
    this.useLocalBackend = !this.useLocalBackend;
    // Save preference to localStorage
    localStorage.setItem('chatBackendPreference', this.useLocalBackend ? 'local' : 'prod');
    this.toastService.showSuccess(`Switched to ${this.agentBackendTargetService.getTarget(this.useLocalBackend).label}`);
    console.log(`Backend URL: ${this.backendUrl}`);
  }

  /**
   * Get the initial backend preference from localStorage or default
   * Defaults to local for localhost, prod for production domains
   */
  private getInitialBackendPreference(): boolean {
    // If not on localhost, always use prod
    if (!this.agentBackendTargetService.isLocalHostname()) {
      return false; // Will use prod backend
    }
    
    // On localhost, check localStorage for saved preference
    const savedPreference = localStorage.getItem('chatBackendPreference');
    if (savedPreference) {
      return savedPreference === 'local';
    }
    
    // Default to local when on localhost and no preference saved
    return true;
  }

  setMode(mode: 'ask' | 'agent') {
    if (this.mode !== mode) {
      this.mode = mode;
      // Removed toast notification for more subtle mode switching
    }
  }

  get hasReadyImageAttachments(): boolean {
    return (this.inputContainer?.getReadyImageAttachments() || this.attachedImageItems)
      .some(item => item.status === 'ready' && !!item.url);
  }

  get hasPendingImageUploads(): boolean {
    return (this.inputContainer?.displayAttachedImageItems || this.attachedImageItems)
      .some(item => item.status === 'uploading');
  }

  get hasReadyFileAttachments(): boolean {
    return (this.inputContainer?.getReadyFileAttachments() || this.attachedFileItems)
      .some(item => item.status === 'ready' && !!item.url);
  }

  get hasPendingFileUploads(): boolean {
    return (this.inputContainer?.displayAttachedFileItems || this.attachedFileItems)
      .some(item => item.status === 'uploading');
  }

  get hasFailedFileUploads(): boolean {
    return (this.inputContainer?.displayAttachedFileItems || this.attachedFileItems)
      .some(item => item.status === 'error');
  }

  get hasFailedImageUploads(): boolean {
    return (this.inputContainer?.displayAttachedImageItems || this.attachedImageItems)
      .some(item => item.status === 'error');
  }

  get selectedModelSupportsImages(): boolean {
    if (!this.selectedModel) {
      return true;
    }

    return this.selectedModel.supportsImages !== false;
  }

  get imageAttachmentValidationMessage(): string {
    if (this.attachedImageItems.length === 0 && this.attachedFileItems.length === 0) {
      return '';
    }

    if (this.hasPendingImageUploads || this.hasPendingFileUploads) {
      return 'Uploading attachments before send.';
    }

    if (this.hasFailedImageUploads || this.hasFailedFileUploads) {
      return 'Remove failed attachments before sending.';
    }

    if (!this.selectedModelSupportsImages && this.selectedModel) {
      return `${this.selectedModel.displayName} does not support image input. Choose a multimodal model.`;
    }

    return '';
  }

  clearSelectedThread() {
    this.currentThreadId = null;
    this.history = [];
    this.chatHistoryLoaded = false;
    this.loading = false; // Reset loading state
    this.isLoadingThread = false;
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
      this.chatSubscription = null;
    }
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
      this.threadSubscription = null;
    }
    this.showOptions = false;
  }

  private async updateThreadName(threadId: string, newName: string): Promise<void> {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      return;
    }

    try {
      await this.afs.collection('threads').doc(threadId).update({
        name: trimmedName,
        updatedAt: new Date()
      });

      const thread = this.availableThreads.find(candidate => candidate.id === threadId);
      if (thread) {
        thread.name = trimmedName;
      }

      this.editingThreadId = null;
    } catch (error: any) {
      console.error('Error renaming thread:', error);
      this.editingThreadId = null;
      this.toastService.showError('Failed to rename thread');
    }
  }

  // Rename thread
  renameThread(threadId: string, newName: string, event: Event) {
    event.stopPropagation();
    void this.updateThreadName(threadId, newName);
  }

  // Start editing thread name
  startEditingThreadName(threadId: string, event: Event) {
    event.stopPropagation();
    this.editingThreadId = threadId;
    
    // Focus the input after the DOM updates
    setTimeout(() => {
      const input = document.querySelector(`[data-thread-id="${threadId}"] .thread-name-input`) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  // Save thread name from input
  saveThreadName(threadId: string, event: Event) {
    event.stopPropagation();
    
    // Get the input value from the current editing field
    const input = document.querySelector(`[data-thread-id="${threadId}"] .thread-name-input`) as HTMLInputElement;
    const newName = input ? input.value.trim() : '';
    
    if (newName) {
      this.renameThread(threadId, newName, event);
    } else {
      // Cancel editing if no name provided
      this.editingThreadId = null;
    }
  }

  // Cancel editing
  cancelEditingThreadName(event: Event) {
    event.stopPropagation();
    this.editingThreadId = null;
  }

  async deleteThread(threadId: string, event: Event, options: { navigateBackOnSuccess?: boolean } = {}) {
    event.stopPropagation(); // Prevent thread selection when deleting

    if (this.isDeletingThread) {
      return;
    }

    const result = await this.alertService.open({
      heading: 'Delete conversation?',
      description: 'This will permanently delete the thread and all of its messages. This action cannot be undone.',
      buttons: [
        { id: 'cancel', label: 'Cancel', icon: 'fa-xmark', color: 'neutral' },
        { id: 'delete', label: 'Delete', icon: 'fa-trash-can', color: 'danger' }
      ]
    });
    if (!result.button || result.button.id !== 'delete') return;

    try {
      this.openThreadDeletionProgressModal();
      this.updateThreadDeletionProgress({
        label: 'Preparing deletion',
        detail: 'Loading the conversation history so it can be removed cleanly.',
        processedCount: 0,
        totalCount: 0
      });

      const chatCollection = this.afs.collection(`threads/${threadId}/chat`);
      const chatSnapshot = await chatCollection.get().toPromise();
      const chatDocs = chatSnapshot?.docs || [];
      const totalMessages = chatDocs.length;

      this.updateThreadDeletionProgress({
        label: totalMessages > 0 ? 'Deleting messages' : 'No messages to delete',
        detail: totalMessages > 0
          ? `Removing ${totalMessages} recorded ${totalMessages === 1 ? 'message' : 'messages'} from this conversation.`
          : 'This conversation has no stored messages, so the thread record is next.',
        processedCount: 0,
        totalCount: totalMessages
      });

      if (chatDocs.length > 0) {
        let deletedCount = 0;

        for (let index = 0; index < chatDocs.length; index += 400) {
          const batchDocs = chatDocs.slice(index, index + 400);
          const batch = this.afs.firestore.batch();

          batchDocs.forEach(doc => {
            batch.delete(doc.ref);
          });

          await batch.commit();
          deletedCount += batchDocs.length;

          this.updateThreadDeletionProgress({
            label: 'Deleting messages',
            detail: `${deletedCount} of ${totalMessages} ${totalMessages === 1 ? 'message' : 'messages'} removed.`,
            processedCount: deletedCount,
            totalCount: totalMessages
          });
        }
      }

      this.updateThreadDeletionProgress({
        label: 'Deleting thread',
        detail: 'Removing the conversation itself and closing it out.',
        processedCount: this.threadDeletionTotalCount,
        totalCount: this.threadDeletionTotalCount
      });

      await this.afs.doc(`threads/${threadId}`).delete();

      if (this.currentThreadId === threadId) {
        this.clearSelectedThread();
      }

      this.closeThreadDeletionProgressModal();
      this.toastService.showSuccess('Thread and all messages deleted successfully');

      if (options.navigateBackOnSuccess) {
        this.navigateBackAfterThreadDeletion();
      }
    } catch (error) {
      this.closeThreadDeletionProgressModal();
      console.error('Error deleting thread:', error);
      this.toastService.showError('Failed to delete thread');
    }
  }

  get hasThreadDeletionCount(): boolean {
    return this.threadDeletionTotalCount > 0;
  }

  get threadDeletionProgressPercent(): number {
    if (!this.threadDeletionTotalCount) {
      return this.isDeletingThread ? 12 : 0;
    }

    return Math.max(12, Math.min(100, Math.round((this.threadDeletionProcessedCount / this.threadDeletionTotalCount) * 100)));
  }

  private openThreadDeletionProgressModal(): void {
    this.isDeletingThread = true;

    if (!this.threadDeleteProgressTemplate) {
      this.cdr.markForCheck();
      return;
    }

    this.uiModalService.open({
      title: 'Deleting conversation',
      subtitle: 'Please keep this open while the conversation is being removed.',
      bodyTemplate: this.threadDeleteProgressTemplate,
      bodyClass: 'thread-delete-progress-modal-body',
      size: 'sm',
      hideFooter: true,
      hideCloseButton: true,
      closeOnBackdropClick: false,
      disableEscapeClose: true
    });

    this.cdr.markForCheck();
  }

  private closeThreadDeletionProgressModal(): void {
    this.isDeletingThread = false;
    this.threadDeletionStatusLabel = '';
    this.threadDeletionStatusDetail = '';
    this.threadDeletionProcessedCount = 0;
    this.threadDeletionTotalCount = 0;
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  private updateThreadDeletionProgress(progress: {
    label: string;
    detail: string;
    processedCount: number;
    totalCount: number;
  }): void {
    this.threadDeletionStatusLabel = progress.label;
    this.threadDeletionStatusDetail = progress.detail;
    this.threadDeletionProcessedCount = progress.processedCount;
    this.threadDeletionTotalCount = progress.totalCount;
    this.cdr.markForCheck();
  }

  private navigateBackAfterThreadDeletion(): void {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      this.location.back();
      return;
    }

    this.clearSelectedThread();
  }

  /**
   * Break out the current thread into a floating window
   */
  breakOutThread(event: Event): void {
    event.stopPropagation();
    
    if (!this.currentThreadId) {
      this.toastService.showError('No thread selected to break out');
      return;
    }

    try {
      // Break out the thread into floating window
      this.floatingThreadService.breakOutThread(this.currentThreadId);
      
      // Clear the current thread from the right panel
      this.currentThreadId = null;
      this.history = [];
      
      // Blur the chat input to remove focus (handled by the component itself)
      
      this.toastService.showSuccess('Thread moved to floating window');
    } catch (error) {
      console.error('Error breaking out thread:', error);
      this.toastService.showError('Failed to break out thread');
    }
  }

  toggleAgentPopover() {
    this.showAgentPopover = !this.showAgentPopover;
  }

  selectAgent(newAgent: string) {
    if (this.agent !== newAgent) {
      this.agent = newAgent;
      if (this.currentThreadId) {
        void this.persistCurrentThreadAgent(newAgent);
      } else {
        this.maybeApplyAgentToolDefaults();
      }
    }
    this.showAgentPopover = false;
  }

  // Methods for empty state shortcut demonstrations
  demonstrateShortcut(type: string): void {
    this.createNewThread();
    setTimeout(() => {
      this.focusInput();
      setTimeout(() => {
        let character: string;
        switch (type) {
          case 'agent':
            character = '@';
            break;
          case 'context':
            character = '#';
            break;
          case 'config':
            character = '+';
            break;
          case 'prompts':
            character = '/';
            break;
          default:
            return;
        }
        
        this.newMessage = character;
        
        // Force change detection and then trigger the popover logic
        setTimeout(() => {
          // Create a mock target with the required properties
          const mockTarget = {
            value: character,
            selectionStart: 1,
            selectionEnd: 1
          };
          
          // Create a mock event with the target
          const mockEvent = {
            target: mockTarget
          };
          
          const inputChangeEvent: InputChangeEvent = {
            value: character,
            event: mockEvent as unknown as Event
          };
          
          this.onInputChange(inputChangeEvent);
        }, 50);
      }, 100);
    }, 100);
  }

  demonstrateKeyboardShortcut(): void {
    setTimeout(() => {
      this.focusInput();
    }, 100);
  }

  closePopovers() {
    // This will close all chat popovers when clicking outside
    const hadCommandPopoverOpen = this.hasCommandPopoverOpen;
    this.showAgentPopover = false;
    this.resetCommandPopovers();

    if (hadCommandPopoverOpen) {
      this.inputContainer?.closeSelectorModal();
    }
  }

  private get hasCommandPopoverOpen(): boolean {
    return this.inputContainer?.hasActiveSelectorModal ||
      this.showPromptPopover ||
      this.showAgentSelectorPopover ||
      this.showContextPopover ||
      this.showModePopover ||
      this.showThreadDetailsPopover;
  }

  private resetCommandPopovers(): void {
    this.showPromptPopover = false;
    this.showAgentSelectorPopover = false;
    this.showContextPopover = false;
    this.showModePopover = false;
    this.showThreadDetailsPopover = false;
    this.commandPopoverSource = 'keyboard';
  }

  private openPromptPopover(): void {
    this.inputContainer?.openPromptPickerModal();
  }

  private openAgentSelectorPopover(): void {
    this.inputContainer?.openAgentPickerModal();
  }

  private openContextPopover(): void {
    this.inputContainer?.openContextPickerModal();
  }

  private openModePopover(): void {
    this.inputContainer?.openModePickerModal();
  }

  private openAddPopover(): void {
    this.inputContainer?.openAddMenuModal();
  }

  private openMoreOptionsPopover(): void {
    this.inputContainer?.openMoreMenuModal();
  }

  private openModelPopover(): void {
    this.inputContainer?.openModelPickerModal();
  }

  private openToolPopover(): void {
    this.inputContainer?.openToolPickerModal();
  }

  private openThreadDetailsPopover(): void {
    this.inputContainer?.openThreadDetailsModal();
  }

  private openComposerShortcutModal(kind: 'add' | 'more' | 'model' | 'tools', openModal: () => void): void {
    if (this.inputContainer?.activeSelectorModal === kind) {
      return;
    }

    this.commandPopoverSource = 'ui';
    this.resetCommandPopovers();

    if (this.inputContainer?.hasActiveSelectorModal) {
      this.inputContainer.closeSelectorModal();
      setTimeout(() => openModal(), 0);
      return;
    }

    openModal();
  }

  private handleOptionShiftComposerShortcut(event: KeyboardEvent): boolean {
    if (!event.altKey || !event.shiftKey || event.metaKey || event.ctrlKey) {
      return false;
    }

    switch (event.code) {
      case 'KeyM':
        event.preventDefault();
        this.openComposerShortcutModal('model', () => this.openModelPopover());
        return true;
      case 'Equal':
        event.preventDefault();
        this.openComposerShortcutModal('add', () => this.openAddPopover());
        return true;
      case 'KeyO':
        event.preventDefault();
        this.openComposerShortcutModal('more', () => this.openMoreOptionsPopover());
        return true;
      case 'KeyT':
        event.preventDefault();
        this.openComposerShortcutModal('tools', () => this.openToolPopover());
        return true;
      case 'KeyR':
        event.preventDefault();
        if (this.loading || this.initializingThread || this.isProcessingTranscription) {
          return true;
        }

        void this.toggleMicrophone();
        return true;
      default:
        return false;
    }
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeyDown(event: KeyboardEvent): void {
    this.handleSelectorModalNumberShortcut(event);
  }

  private handleSelectorModalNumberShortcut(event: KeyboardEvent): boolean {
    const activeSelectorModal = this.inputContainer?.activeSelectorModal;
    if (activeSelectorModal !== 'add' && activeSelectorModal !== 'more') {
      return false;
    }

    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return false;
    }

    const position = this.getShortcutDigitFromKeyboardEvent(event);
    if (!position) {
      return false;
    }

    const handled = this.inputContainer?.activateSelectorModalOptionByNumber(position) || false;
    if (!handled) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  private getShortcutDigitFromKeyboardEvent(event: KeyboardEvent): number | null {
    if (/^Digit[1-9]$/.test(event.code)) {
      return Number(event.code.replace('Digit', ''));
    }

    if (/^Numpad[1-9]$/.test(event.code)) {
      return Number(event.code.replace('Numpad', ''));
    }

    return null;
  }

  private loadAvailableModels(preferredModelKey?: string): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    const currentPreferredKey = preferredModelKey || this.selectedModel?.key || this.getThreadPreferredModelKey(this.currentThread);
    const selectedBackendUrl = this.backendUrl;
    const selectedBackendLabel = this.getBackendLabel(selectedBackendUrl);

    this.modelCatalogStatus = 'loading';
    this.modelCatalogSourceLabel = selectedBackendLabel;
    this.modelCatalogMessage = `Resolving the model catalog for ${selectedBackendLabel}.`;
    console.log('[model-picker] resolving model catalog via cache service (backend call only if agent-catalog-cache logs backend fetch)', {
      backendUrl: selectedBackendUrl,
      backendLabel: selectedBackendLabel,
      preferredModelKey: currentPreferredKey
    });

    return this.fetchModelCatalog(selectedBackendUrl)
      .then(result => {
        const response = result.data;
        const liveModels = (response?.models ?? [])
          .map(model => this.toModelOption(model))
          .sort((left, right) => left.displayName.localeCompare(right.displayName));
        const liveVideoModels = (response?.videoModels ?? [])
          .map(model => this.toVideoModelOption(model))
          .sort((left, right) => left.displayName.localeCompare(right.displayName));

        if (liveModels.length === 0) {
          throw new Error('Model catalog response was empty.');
        }

        this.availableModels = liveModels;
        this.availableVideoModels = liveVideoModels;
        this.defaultAudioModelKey = this.normalizePreferenceString(response?.defaultAudioModel);
        this.defaultAudioVoice = this.normalizePreferenceString(response?.defaultAudioVoice);
        this.defaultImageModelKey = this.normalizePreferenceString(response?.defaultImageModel);
        this.defaultVideoModelKey = this.normalizePreferenceString(response?.defaultVideoModel);
        this.audioVoiceOptionsByModel = response?.audioVoiceOptionsByModel ?? {};
        this.providerRecommendations = response?.providerRecommendations ?? [];
        this.modelCatalogStatus = 'live';
        this.modelCatalogSourceLabel = selectedBackendLabel;
        this.modelCatalogMessage = `Live catalog loaded from ${selectedBackendLabel}.`;
        console.log('[model-picker] model catalog resolved', {
          backendLabel: selectedBackendLabel,
          source: result.source,
          modelCount: liveModels.length,
          selectedModelKey: currentPreferredKey
        });

        const nextSelectedModel = this.findAvailableModel(currentPreferredKey)
          || this.findAvailableModel(this.getThreadPreferredModelKey(this.currentThread))
          || this.findAvailableModel(response?.defaultModel)
          || this.availableModels[0];

        this.selectedModel = nextSelectedModel;
        this.cdr.detectChanges();
      })
      .catch(error => {
        console.warn(`[model-picker] Failed to load models from ${selectedBackendLabel}:`, this.formatModelCatalogError(error));

        this.availableModels = [];
        this.availableVideoModels = [];
        this.defaultAudioModelKey = '';
        this.defaultAudioVoice = '';
        this.defaultImageModelKey = '';
        this.defaultVideoModelKey = '';
        this.audioVoiceOptionsByModel = {};
        this.providerRecommendations = [];
        this.modelCatalogStatus = 'error';
        this.modelCatalogSourceLabel = selectedBackendLabel;
        this.modelCatalogMessage = `Could not load /agent/models from ${selectedBackendLabel}. ${this.formatModelCatalogError(error)}`;
        this.selectedModel = undefined;
        this.cdr.detectChanges();
      });
  }

  private fetchModelCatalog(baseUrl: string, forceRefresh = false): Promise<CatalogCacheResult<AgentModelsResponse>> {
    return this.agentCatalogCache.getModelsResult(baseUrl, forceRefresh);
  }

  private loadAvailableTools(preferredToolNames?: string[]): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    const selectedBackendUrl = this.backendUrl;
    const selectedBackendLabel = this.getBackendLabel(selectedBackendUrl);

    this.toolCatalogStatus = 'loading';
    this.toolCatalogSourceLabel = selectedBackendLabel;
    this.toolCatalogMessage = `Resolving the tool catalog for ${selectedBackendLabel}.`;
    console.log('[tool-picker] resolving tool catalog via cache service (backend call only if agent-catalog-cache logs backend fetch)', {
      backendUrl: selectedBackendUrl,
      backendLabel: selectedBackendLabel,
      selectedToolNames: preferredToolNames ?? this.selectedToolNames
    });

    return this.fetchToolCatalog(selectedBackendUrl)
      .then(result => {
        const response = result.data;
        const responseTools = response?.tools ?? [];
        const isLegacyCatalog = isLegacyToolCatalogResponse(responseTools);
        const liveTools = responseTools
          .map(tool => this.toToolOption(tool))
          .sort((left, right) => left.displayName.localeCompare(right.displayName));

        this.availableTools = liveTools;
        const pendingPlaybookToolNames = this.resolvePlaybookToolNames(this.pendingPlaybookToolGroupKeys, liveTools);
        const nextPreferredToolNames = preferredToolNames
          ?? (pendingPlaybookToolNames.length > 0 && !this.hasManualToolSelection ? pendingPlaybookToolNames : this.selectedToolNames);

        this.selectedToolNames = this.normalizeToolNames(nextPreferredToolNames)
          .filter(name => liveTools.some(tool => tool.name === name));
        if (pendingPlaybookToolNames.length > 0) {
          this.pendingPlaybookToolGroupKeys = [];
        }
        this.refreshDefaultToolNames();
        this.maybeApplyAgentToolDefaults();
        this.toolSelectionMode = inferToolSelectionMode(this.selectedToolNames, this.defaultToolNames);
        this.toolCatalogStatus = 'live';
        this.toolCatalogSourceLabel = selectedBackendLabel;
        this.toolCatalogMessage = isLegacyCatalog
          ? getLegacyToolCatalogMessage(selectedBackendUrl, responseTools)
          : `Live catalog loaded from ${selectedBackendLabel}.`;
        console.log('[tool-picker] tool catalog resolved', {
          backendLabel: selectedBackendLabel,
          source: result.source,
          toolCount: liveTools.length
        });
        this.cdr.detectChanges();
      })
      .catch(error => {
        console.warn(`[tool-picker] Failed to load tools from ${selectedBackendLabel}:`, this.formatToolCatalogError(error));

        this.availableTools = [];
        this.toolCatalogStatus = 'error';
        this.toolCatalogSourceLabel = selectedBackendLabel;
        this.toolCatalogMessage = `Could not load /agent/tools from ${selectedBackendLabel}. ${this.formatToolCatalogError(error)}`;
        this.cdr.detectChanges();
      });
  }

  private fetchToolCatalog(baseUrl: string, forceRefresh = false): Promise<CatalogCacheResult<{ tools?: ToolInfo[]; count?: number }>> {
    return this.agentCatalogCache.getToolsResult(baseUrl, forceRefresh);
  }

  private getBackendLabel(url: string): string {
    return this.agentBackendTargetService.getBackendLabel(url);
  }

  private formatModelCatalogError(error: unknown): string {
    const message = typeof error === 'object' && error && 'message' in error
      ? String((error as { message?: unknown }).message || '')
      : '';

    if (message.includes('Timeout has occurred')) {
      return 'The request timed out after 8 seconds.';
    }

    if (message) {
      return message;
    }

    return 'Start the selected backend and reopen the picker to retry.';
  }

  private formatToolCatalogError(error: unknown): string {
    const message = typeof error === 'object' && error && 'message' in error
      ? String((error as { message?: unknown }).message || '')
      : '';

    if (message.includes('Timeout has occurred')) {
      return 'The request timed out after 8 seconds.';
    }

    if (message) {
      return message;
    }

    return 'Start the selected backend and reopen the picker to retry.';
  }

  private toModelOption(model: OpenRouterModelSummary): ModelOption {
    const provider = this.getModelProvider(model.id);
    const supportedParameters = model.supported_parameters ?? [];
    const architecture = model.architecture as { input_modalities?: string[]; output_modalities?: string[] } | undefined;
    const inputModalities = architecture?.input_modalities ?? ['text'];
    const outputModalities = architecture?.output_modalities ?? ['text'];
    const promptPrice = model.pricing?.prompt ? Number(model.pricing.prompt) : undefined;
    const completionPrice = model.pricing?.completion ? Number(model.pricing.completion) : undefined;
    const aliasKeys = [model.canonical_slug?.trim()]
      .filter((value): value is string => !!value && value !== model.id);

    return {
      key: model.id,
      displayName: model.name?.trim() || formatModelDisplayName(model.id),
      category: provider.toUpperCase(),
      provider,
      canonicalSlug: model.canonical_slug?.trim() || undefined,
      description: model.description?.trim() || undefined,
      created: model.created,
      knowledgeCutoff: model.knowledge_cutoff ?? null,
      expirationDate: model.expiration_date ?? null,
      deprecationDate: model.deprecation_date,
      contextLength: model.context_length,
      aliasKeys: aliasKeys.length > 0 ? aliasKeys : undefined,
      promptPrice,
      completionPrice,
      inputModalities,
      outputModalities,
      supportedParameters,
      supportsImages: inputModalities.some(modality => modality === 'image' || modality === 'file'),
      supportsTools: supportedParameters.length === 0 || supportedParameters.includes('tools') || supportedParameters.includes('tool_choice') || supportedParameters.includes('parallel_tool_calls'),
      rawSummary: model
    };
  }

  private toVideoModelOption(model: OpenRouterModelSummary): ModelOption {
    return {
      ...this.toModelOption(model),
      inputModalities: ['text'],
      outputModalities: ['video'],
      supportsImages: false,
      supportsTools: false
    };
  }

  private toToolOption(tool: ToolInfo): ToolOption {
    const compatibility = getToolCatalogCompatibilityMetadata(tool);
    const title = typeof tool.definition?.title === 'string' ? tool.definition.title.trim() : (compatibility.title || '');
    const displayName = title || this.formatToolDisplayName(tool.name);

    return {
      name: tool.name,
      displayName,
      title: title || undefined,
      description: tool.definition?.description?.trim() || compatibility.groupDescription || tool.description?.trim() || displayName,
      category: tool.definition?.category || compatibility.category,
      groupKey: tool.definition?.group?.key || compatibility.groupKey,
      groupLabel: tool.definition?.group?.label || compatibility.groupLabel,
      groupDescription: tool.definition?.group?.description || compatibility.groupDescription,
      groupOrder: tool.definition?.group?.order || compatibility.groupOrder,
      readOnly: tool.definition?.annotations?.readOnly,
      destructive: tool.definition?.annotations?.destructive,
      requiresConfirmation: tool.definition?.annotations?.requiresConfirmation,
      rawInfo: tool
    };
  }

  private formatToolDisplayName(name: string): string {
    return String(name || '')
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(part => {
        const normalized = part.toLowerCase();
        if (normalized === 'uid') return 'UID';
        if (normalized === 'id') return 'ID';
        if (normalized === 'url') return 'URL';
        if (normalized === 'api') return 'API';
        if (normalized === 'ai') return 'AI';
        if (normalized === 'seo') return 'SEO';
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(' ');
  }

  private findAvailableModel(modelKey?: string): ModelOption | undefined {
    const canonicalKey = getCanonicalModelKey(modelKey);
    const candidateKeys = [modelKey, canonicalKey].filter((value): value is string => !!value);

    return this.availableModels.find(model =>
      candidateKeys.includes(model.key) ||
      candidateKeys.some(candidateKey => model.aliasKeys?.includes(candidateKey))
    );
  }

  private getModelProvider(modelKey: string): string {
    const [provider] = modelKey.split('/');
    return provider || 'openrouter';
  }

  // Event handlers for subcomponents
  onBackendToggled(useLocalBackend: boolean): void {
    this.useLocalBackend = useLocalBackend;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.chatBackendPreferenceKey, this.useLocalBackend ? 'local' : 'prod');
    }
    this.loadAvailableModels(this.selectedModel?.key);
    this.loadAvailableTools(this.selectedToolNames);
  }

  onDisplayModePreferenceChanged(displayMode: ChatDisplayMode): void {
    this.displayMode = displayMode;

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.chatDisplayModePreferenceKey, displayMode);
    }
  }

  onRenderPreviewsInlinePreferenceChanged(renderPreviewsInline: boolean): void {
    this.renderPreviewsInline = renderPreviewsInline;

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.chatRenderPreviewsInlinePreferenceKey, renderPreviewsInline ? 'true' : 'false');
    }
  }

  private getInitialRenderPreviewsInlinePreference(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    return localStorage.getItem(this.chatRenderPreviewsInlinePreferenceKey) === 'true';
  }

  onShortcutDemonstrated(demonstration: ShortcutDemonstration): void {
    this.demonstrateShortcut(demonstration.type);
  }

  onStarterPromptClick(prompt: string): void {
    // Set the input value and trigger send
    this.newMessage = prompt;
    this.sendMessage();
  }

  onSuggestedFollowUpSelected(prompt: string): void {
    this.newMessage = prompt;
    this.sendMessage();
  }

  onTranscriptionCancelled(): void {
    this.cancelTranscription();
  }

  onModeSelected(modeOption: ModeOption): void {
    this.selectModeFromPopover(modeOption as any); // Cast to match existing signature
  }

  onAgentSelected(agentOption: AgentOption): void {
    this.selectAgentFromPopover(agentOption);
  }

  onContextItemSelected(item: ContextItem): void {
    this.selectContextItem(item);
  }

  onThreadSelected(thread: any): void {
    this.selectThread(thread?.id || '');
  }

  onBreakOutThread(event: Event): void {
    this.breakOutThread(event);
  }

  onCreateNewThread(): void {
    this.createNewThread();
  }

  onDeleteThread(actionEvent: ThreadActionEvent): void {
    this.deleteThread(actionEvent.threadId, actionEvent.event);
  }

  onDeleteCurrentThreadRequested(): void {
    if (!this.currentThreadId) {
      return;
    }

    void this.deleteThread(this.currentThreadId, { stopPropagation() {} } as Event, {
      navigateBackOnSuccess: true
    });
  }

  async onForceCompactionRequested(): Promise<void> {
    if (!this.currentThreadId) {
      this.toastService.showError('No thread selected');
      return;
    }

    if (this.forceCompactionLoading) {
      return;
    }

    this.forceCompactionLoading = true;
    try {
      const response = await firstValueFrom(this.http.post<ForceCompactionResponse>(
        `${this.backendUrl}/agent/thread/${this.currentThreadId}/compact`,
        {
          reason: 'User requested manual compaction from the context window control.',
          model: this.selectedModel?.key || this.getThreadPreferredModelKey(this.currentThread),
          preserveRecentUserTurns: 1,
          targetSummaryTokens: 1800
        }
      ));

      if (response.compacted) {
        this.toastService.showSuccess(`Compacted ${response.compactedMessageCount || 0} messages`);
      } else {
        this.toastService.showError(response.reason || 'No eligible messages were available to compact');
      }
    } catch (error) {
      console.error('Failed to force compaction:', error);
      this.toastService.showError('Failed to force compaction');
    } finally {
      this.forceCompactionLoading = false;
      this.cdr.markForCheck();
    }
  }

  onRenameCurrentThreadRequested(newName: string): void {
    if (!this.currentThreadId) {
      return;
    }

    void this.updateThreadName(this.currentThreadId, newName);
  }

  onViewOptionSelected(action: ChatThreadViewAction): void {
    void this.applyThreadViewAction(action);
  }

  async onEditTodoItemRequested(updatedItem: AgentTodoItem): Promise<void> {
    if (this.displayMode !== 'debug') {
      return;
    }

    if (!this.currentThreadId) {
      this.toastService.showError('No thread selected');
      return;
    }

    try {
      const items = this.getCurrentTodoItems();
      const existingItem = items.find(item => item.id === updatedItem.id);
      if (!existingItem) {
        this.toastService.showError('To-do not found');
        return;
      }

      const nextItems = items.map(item => {
        if (item.id !== updatedItem.id) {
          return this.normalizeTodoItemForStorage(item);
        }

        const now = new Date();
        const mergedItem: AgentTodoItem = {
          ...existingItem,
          title: updatedItem.title.trim(),
          status: updatedItem.status,
          notes: typeof updatedItem.notes === 'string' ? updatedItem.notes.trim() : undefined,
          updatedAt: now,
          completedAt: updatedItem.status === 'completed'
            ? existingItem.completedAt || now
            : undefined
        };

        return this.normalizeTodoItemForStorage(mergedItem);
      });

      await this.updateCurrentThreadTodoItems(nextItems);
      this.toastService.showSuccess('To-do updated');
    } catch (error) {
      console.error('Error updating to-do:', error);
      this.toastService.showError('Failed to update to-do');
    }
  }

  async onDeleteTodoItemRequested(item: AgentTodoItem): Promise<void> {
    if (this.displayMode !== 'debug') {
      return;
    }

    if (!this.currentThreadId) {
      this.toastService.showError('No thread selected');
      return;
    }

    const result = await this.alertService.open({
      heading: 'Delete to-do?',
      description: `This will remove "${item.title}" from this thread's to-do list.`,
      buttons: [
        { id: 'cancel', label: 'Cancel', icon: 'fa-xmark', color: 'neutral' },
        { id: 'delete', label: 'Delete', icon: 'fa-trash-can', color: 'danger' }
      ]
    });

    if (!result.button || result.button.id !== 'delete') return;

    try {
      const remainingItems = this.getCurrentTodoItems().filter(existingItem => existingItem.id !== item.id);
      await this.updateCurrentThreadTodoItems(remainingItems);
      this.toastService.showSuccess('To-do deleted');
    } catch (error) {
      console.error('Error deleting to-do:', error);
      this.toastService.showError('Failed to delete to-do');
    }
  }

  async onResetTodoListRequested(): Promise<void> {
    if (this.displayMode !== 'debug') {
      return;
    }

    if (!this.currentThreadId) {
      this.toastService.showError('No thread selected');
      return;
    }

    const result = await this.alertService.open({
      heading: 'Delete to-do list?',
      description: 'This will remove every to-do item from this thread.',
      buttons: [
        { id: 'cancel', label: 'Cancel', icon: 'fa-xmark', color: 'neutral' },
        { id: 'delete', label: 'Delete all', icon: 'fa-trash-can', color: 'danger' }
      ]
    });

    if (!result.button || result.button.id !== 'delete') return;

    try {
      await this.updateCurrentThreadTodoItems([]);
      this.toastService.showSuccess('To-do list deleted');
    } catch (error) {
      console.error('Error deleting to-do list:', error);
      this.toastService.showError('Failed to delete to-do list');
    }
  }

  private getCurrentTodoItems(): AgentTodoItem[] {
    return Array.isArray(this.currentThread?.todoList?.items)
      ? this.currentThread.todoList.items.filter((item: any): item is AgentTodoItem => !!item?.id && !!item?.title)
      : [];
  }

  private normalizeTodoItemForStorage(item: AgentTodoItem): AgentTodoItem {
    const normalized: AgentTodoItem = {
      id: item.id,
      title: item.title.trim(),
      status: item.status
    };

    if (item.createdAt) {
      normalized.createdAt = item.createdAt;
    }

    if (item.updatedAt) {
      normalized.updatedAt = item.updatedAt;
    }

    const notes = typeof item.notes === 'string' ? item.notes.trim() : '';
    if (notes) {
      normalized.notes = notes;
    }

    if (item.status === 'completed' && item.completedAt) {
      normalized.completedAt = item.completedAt;
    }

    return normalized;
  }

  private async updateCurrentThreadTodoItems(items: AgentTodoItem[]): Promise<void> {
    if (!this.currentThreadId) {
      throw new Error('No thread selected');
    }

    await this.afs.doc(`threads/${this.currentThreadId}`).update({
      todoList: {
        items: items.map(item => this.normalizeTodoItemForStorage(item)),
        updatedAt: new Date()
      },
      updatedAt: new Date()
    });
  }

  async onDeleteMessage(deleteEvent: { messageIndex: number, messageId?: string }): Promise<void> {
    if (!this.currentThreadId) {
      this.toastService.showError('No thread selected');
      return;
    }

    const messageToDelete = this.history[deleteEvent.messageIndex];
    if (!messageToDelete) {
      this.toastService.showError('Message not found');
      return;
    }

    const isCompactionMarker = this.isCompactionMarkerMessage(messageToDelete);
    const result = await this.alertService.open({
      heading: isCompactionMarker ? 'Delete compaction?' : 'Delete message?',
      description: isCompactionMarker
        ? 'This will remove the compacted summary. Future model calls will treat the conversation as if this compaction marker was never created.'
        : 'This will permanently delete this message from the conversation. This action cannot be undone.',
      buttons: [
        { id: 'cancel', label: 'Cancel', icon: 'fa-xmark', color: 'neutral' },
        { id: 'delete', label: 'Delete', icon: 'fa-trash-can', color: 'danger' }
      ]
    });
    
    if (!result.button || result.button.id !== 'delete') return;

    try {
      // If we have a message ID, try to delete from Firestore
      if (deleteEvent.messageId) {
        await this.afs.doc(`threads/${this.currentThreadId}/chat/${deleteEvent.messageId}`).delete();
      } else {
        // For messages without ID, we need to find and delete them by content/timestamp
        // This is more complex and might require querying the collection
        const chatCollection = this.afs.collection(`threads/${this.currentThreadId}/chat`);
        
        // Get the content for comparison - could be string or complex object from responses API
        let contentForComparison = messageToDelete.content;
        if (typeof contentForComparison === 'object') {
          // For complex content objects, convert to JSON string for comparison
          contentForComparison = JSON.stringify(contentForComparison);
        }
        
        const snapshot = await chatCollection.ref
          .where('role', '==', messageToDelete.role || messageToDelete.originalRole)
          .where('content', '==', contentForComparison)
          .limit(1)
          .get();
        
        if (!snapshot.empty) {
          await snapshot.docs[0].ref.delete();
        } else {
          // If we can't find the message in Firestore, just remove from local history
          this.history.splice(deleteEvent.messageIndex, 1);
          this.toastService.showSuccess('Message removed from local view');
          return;
        }
      }

      if (isCompactionMarker) {
        await this.clearThreadCompactedTimestampIfNeeded();
      }

      this.toastService.showSuccess(isCompactionMarker ? 'Compaction deleted successfully' : 'Message deleted successfully');
    } catch (error) {
      console.error('Error deleting message:', error);
      this.toastService.showError(isCompactionMarker ? 'Failed to delete compaction' : 'Failed to delete message');
    }
  }

  private isCompactionMarkerMessage(message: any): boolean {
    const ui = message?.ui && typeof message.ui === 'object' && !Array.isArray(message.ui)
      ? message.ui
      : {};

    return message?.role === 'system'
      && message?.name === 'context_compaction'
      && ui.kind === 'context_compaction';
  }

  private async clearThreadCompactedTimestampIfNeeded(): Promise<void> {
    if (!this.currentThreadId) {
      return;
    }

    const remainingSnapshot = await this.afs.collection(`threads/${this.currentThreadId}/chat`, ref =>
      ref.where('role', '==', 'system')
    ).get().toPromise();

    const hasRemainingCompaction = !!remainingSnapshot?.docs.some(doc => this.isCompactionMarkerMessage(doc.data()));

    if (!hasRemainingCompaction) {
      await this.afs.doc(`threads/${this.currentThreadId}`).update({
        lastCompactedAt: null
      });
    }
  }

  async onUpdateMessage(updateEvent: ChatMessageUpdateEvent): Promise<void> {
    if (!this.currentThreadId) {
      this.toastService.showError('No thread selected');
      return;
    }

    const messageToUpdate = this.history[updateEvent.messageIndex];
    if (!messageToUpdate) {
      this.toastService.showError('Message not found');
      return;
    }

    const updateData = this.buildMessageContentUpdate(messageToUpdate, updateEvent.content);

    try {
      if (updateEvent.messageId) {
        await this.afs.doc(`threads/${this.currentThreadId}/chat/${updateEvent.messageId}`).update(updateData);
      } else {
        this.history = this.history.map((message, index) =>
          index === updateEvent.messageIndex
            ? this.applyMessageContentUpdate(message, updateEvent.content)
            : message
        );
        this.toastService.showSuccess('Message updated in local view');
        return;
      }

      this.toastService.showSuccess('Message updated successfully');
    } catch (error) {
      console.error('Error updating message:', error);
      this.toastService.showError('Failed to update message');
    }
  }

  private buildMessageContentUpdate(message: any, content: string): Record<string, unknown> {
    if (message?.message && Object.prototype.hasOwnProperty.call(message.message, 'content')) {
      return {
        'message.content': this.replaceMessageTextContent(message.message.content, content)
      };
    }

    return {
      content: this.replaceMessageTextContent(message?.content, content)
    };
  }

  private applyMessageContentUpdate(message: any, content: string): any {
    if (message?.message && Object.prototype.hasOwnProperty.call(message.message, 'content')) {
      return {
        ...message,
        message: {
          ...message.message,
          content: this.replaceMessageTextContent(message.message.content, content)
        }
      };
    }

    return {
      ...message,
      content: this.replaceMessageTextContent(message?.content, content)
    };
  }

  private replaceMessageTextContent(existingContent: any, content: string): any {
    if (Array.isArray(existingContent)) {
      let replaced = false;
      const updatedBlocks = existingContent.map((block: any) => {
        if (replaced) {
          return block;
        }

        if (typeof block === 'string') {
          replaced = true;
          return content;
        }

        if (block && typeof block === 'object') {
          if (typeof block.text === 'string') {
            replaced = true;
            return { ...block, text: content };
          }

          if (typeof block.output_text === 'string') {
            replaced = true;
            return { ...block, output_text: content };
          }
        }

        return block;
      });

      return replaced ? updatedBlocks : [{ type: 'text', text: content }, ...existingContent];
    }

    if (existingContent && typeof existingContent === 'object') {
      if (typeof existingContent.text === 'string') {
        return { ...existingContent, text: content };
      }

      if (typeof existingContent.output_text === 'string') {
        return { ...existingContent, output_text: content };
      }
    }

    return content;
  }

  private getDetachedThreadState(): FloatingThreadState | null {
    if (!this.currentThreadId) {
      return null;
    }

    return this.floatingThreadService.getFloatingThreads().find(thread => thread.threadId === this.currentThreadId) || null;
  }

  private getCurrentThreadView(): 'inline' | 'page' | 'floating' | 'pinned' | 'fullscreen' {
    if (this.fullPageLayout) {
      return 'page';
    }

    const detachedThread = this.getDetachedThreadState();
    if (!detachedThread) {
      return 'inline';
    }

    if (detachedThread.isMaximized) {
      return 'fullscreen';
    }

    if (detachedThread.displayMode === 'pinned') {
      return 'pinned';
    }

    return 'floating';
  }

  private canUsePinnedView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth > 768;
  }

  private async applyThreadViewAction(action: ChatThreadViewAction): Promise<void> {
    switch (action) {
      case 'page':
        await this.openThreadOnPage();
        return;
      case 'floating':
        await this.moveThreadToPresentation('floating');
        return;
      case 'pinned':
        await this.moveThreadToPresentation('pinned');
        return;
      case 'fullscreen':
        await this.moveThreadToPresentation('fullscreen');
        return;
      case 'minimize':
        await this.minimizeCurrentThread();
        return;
    }
  }

  private async openThreadOnPage(): Promise<void> {
    if (!this.currentThreadId || this.fullPageLayout) {
      return;
    }

    const queryParams: Record<string, string> = {};
    const agencyId = this.resolveAgencyId();
    const returnTo = this.getThreadReturnUrl();

    if (agencyId) {
      queryParams['uid'] = agencyId;
    }

    if (returnTo) {
      queryParams['returnTo'] = returnTo;
    }

    if (this.getDetachedThreadState()) {
      this.floatingThreadService.closeThread(this.currentThreadId);
    }

    await this.router.navigate(['/myagency/agents', this.currentThreadId], { queryParams });
  }

  private async moveThreadToPresentation(presentation: FloatingThreadPresentation): Promise<void> {
    if (!this.currentThreadId) {
      return;
    }

    if (presentation === 'pinned' && !this.canUsePinnedView()) {
      return;
    }

    const threadId = this.currentThreadId;

    if (this.fullPageLayout) {
      const targetUrl = this.getReturnUrlFromRoute();
      const didNavigate = this.router.url === targetUrl ? true : await this.router.navigateByUrl(targetUrl);

      if (!didNavigate) {
        return;
      }

      setTimeout(() => {
        this.floatingThreadService.showThread(threadId, presentation);
      }, 0);
      return;
    }

    this.floatingThreadService.showThread(threadId, presentation);

    if (!this.inFloatingPanel) {
      this.clearSelectedThread();
    }
  }

  private async minimizeCurrentThread(): Promise<void> {
    if (!this.currentThreadId) {
      return;
    }

    const threadId = this.currentThreadId;

    if (this.fullPageLayout) {
      const targetUrl = this.getReturnUrlFromRoute();
      const didNavigate = this.router.url === targetUrl ? true : await this.router.navigateByUrl(targetUrl);

      if (!didNavigate) {
        return;
      }

      setTimeout(() => {
        this.floatingThreadService.showThread(threadId, 'floating');
        this.floatingThreadService.toggleMinimize(threadId);
      }, 0);
      return;
    }

    if (!this.getDetachedThreadState()) {
      this.floatingThreadService.showThread(threadId, 'floating');
    }

    this.floatingThreadService.toggleMinimize(threadId);

    if (!this.inFloatingPanel) {
      this.clearSelectedThread();
    }
  }

  private getThreadReturnUrl(): string {
    if (this.fullPageLayout) {
      return this.getReturnUrlFromRoute();
    }

    const currentUrl = this.router.url || '';
    if (currentUrl && !currentUrl.startsWith('/myagency/agents/')) {
      return currentUrl;
    }

    return this.getDefaultReturnUrl();
  }

  private getReturnUrlFromRoute(): string {
    const currentUrl = this.router.url || '';
    const queryString = currentUrl.split('?')[1] || '';
    const params = new URLSearchParams(queryString);

    return params.get('returnTo') || this.getDefaultReturnUrl();
  }

  private getDefaultReturnUrl(): string {
    return '/myagency';
  }

  onMessageChanged(inputEvent: InputChangeEvent): void {
    this.newMessage = inputEvent.value;
    this.onInputChange(inputEvent);
  }

  onMessageSent(): void {
    this.sendMessage();
  }

  onExpandToggled(expanded: boolean): void {
    this.isExpanded = expanded;
  }

  onMicrophoneToggled(): void {
    this.toggleMicrophone();
  }

  onInputKeyDownEvent(keyEvent: ChatKeyboardEvent): void {
    this.onInputKeyDown(keyEvent.event);
  }

  onTextareaKeyDownEvent(keyEvent: ChatKeyboardEvent): void {
    this.onTextareaKeyDown(keyEvent.event);
  }

  onAddMenuRequested(): void {
    // Now handled by input-container internally
  }

  onAgentChangedFromComposer(agentValue: string): void {
    this.selectAgent(agentValue);
  }

  onModeChangedFromComposer(modeValue: 'ask' | 'agent'): void {
    this.setMode(modeValue);
  }

  async onModelCatalogRefreshRequested(forceRefresh = false): Promise<void> {
    if (forceRefresh) {
      this.agentCatalogCache.clearCache();
    }
    this.modelCatalogStatus = 'loading';
    this.modelCatalogMessage = 'Requesting the live model catalog from the selected backend.';
    await this.loadAvailableModels(this.selectedModel?.key);
    this.cdr.detectChanges();
    console.log('[model-picker] refresh finished', {
      status: this.modelCatalogStatus,
      availableModels: this.availableModels.length,
      source: this.modelCatalogSourceLabel
    });
  }

  async onToolCatalogRefreshRequested(forceRefresh = false): Promise<void> {
    if (forceRefresh) {
      this.agentCatalogCache.clearCache();
    }
    this.toolCatalogStatus = 'loading';
    this.toolCatalogMessage = 'Requesting the live tool catalog from the selected backend.';
    await this.loadAvailableTools(this.selectedToolNames);
    this.cdr.detectChanges();
    console.log('[tool-picker] refresh finished', {
      status: this.toolCatalogStatus,
      availableTools: this.availableTools.length,
      source: this.toolCatalogSourceLabel
    });
  }

  onModelChangedFromComposer(model: ModelOption): void {
    this.selectedModel = model;
  }

  onToolSelectionChangedFromComposer(toolNames: string[]): void {
    this.pendingPlaybookToolGroupKeys = [];
    this.selectedToolNames = this.normalizeToolNames(toolNames);
    this.refreshDefaultToolNames();
    this.toolSelectionMode = inferToolSelectionMode(this.selectedToolNames, this.defaultToolNames);
    this.hasManualToolSelection = this.toolSelectionMode === 'custom';
  }

  onPlaybookActivated(playbook: PlaybookEntry): void {
    this.applyPlaybookExecutionDefaults(playbook);
  }

  onAudioModelChangedFromComposer(modelKey: string): void {
    this.selectedAudioModelKey = this.normalizePreferenceString(modelKey);
  }

  onImageModelChangedFromComposer(modelKey: string): void {
    this.selectedImageModelKey = this.normalizePreferenceString(modelKey);
  }

  onVideoModelChangedFromComposer(modelKey: string): void {
    this.selectedVideoModelKey = this.normalizePreferenceString(modelKey);
  }

  onAudioVoiceChangedFromComposer(voice: string): void {
    this.selectedAudioVoice = this.normalizePreferenceString(voice);
  }

  onComposerActionSelected(action: 'prompts' | 'agent' | 'context' | 'settings' | 'model' | 'details'): void {
    const cursorPosition = this.inputContainer?.getCursorPosition() ?? this.newMessage.length;
    this.commandPopoverSource = 'ui';

    switch (action) {
      case 'prompts':
        this.promptCursorPosition = cursorPosition;
        this.selectedPromptIndex = 0;
        this.showPromptPopover = true;
        this.showAgentSelectorPopover = false;
        this.showContextPopover = false;
        this.showModePopover = false;
        this.showThreadDetailsPopover = false;
        this.openPromptPopover();
        break;
      case 'agent':
        this.agentCursorPosition = cursorPosition;
        this.selectedAgentIndex = this.availableAgents.findIndex(agent => agent.value === this.agent);
        if (this.selectedAgentIndex === -1) this.selectedAgentIndex = 0;
        this.showAgentSelectorPopover = true;
        this.showPromptPopover = false;
        this.showContextPopover = false;
        this.showModePopover = false;
        this.showThreadDetailsPopover = false;
        this.openAgentSelectorPopover();
        break;
      case 'context':
        this.contextCursorPosition = cursorPosition;
        this.selectedContextIndex = 0;
        this.showContextPopover = true;
        this.showPromptPopover = false;
        this.showAgentSelectorPopover = false;
        this.showModePopover = false;
        this.showThreadDetailsPopover = false;
        this.openContextPopover();
        break;
      case 'settings':
        this.modeCursorPosition = cursorPosition;
        this.selectedModeIndex = this.mode === 'ask' ? 0 : 1;
        this.showModePopover = true;
        this.showPromptPopover = false;
        this.showAgentSelectorPopover = false;
        this.showContextPopover = false;
        this.showThreadDetailsPopover = false;
        this.openModePopover();
        break;
      case 'model':
        this.modeCursorPosition = cursorPosition;
        this.showModePopover = false;
        this.showPromptPopover = false;
        this.showAgentSelectorPopover = false;
        this.showContextPopover = false;
        this.showThreadDetailsPopover = false;
        this.openModelPopover();
        break;
      case 'details':
        this.threadDetailsCursorPosition = cursorPosition;
        this.showThreadDetailsPopover = true;
        this.showPromptPopover = false;
        this.showAgentSelectorPopover = false;
        this.showContextPopover = false;
        this.showModePopover = false;
        this.openThreadDetailsPopover();
        break;
    }
  }

  changeAgent(newAgent: any) {
    if (newAgent !== this.agent) {
      this.agent = newAgent;
      // Reset the chat when agent changes
      this.refresh();
      this.toastService.showSuccess(`Switched to ${this.returnAiAssName()}`);
    }
  }

  async toggleMicrophone() {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      // Set connecting state immediately for instant feedback
      this.isConnecting = true;
      try {
        await this.startRecording();
        // Once recording starts successfully, clear connecting state
        this.isConnecting = false;
      } catch (error) {
        // If recording fails, clear connecting state
        this.isConnecting = false;
        this.toastService.showError('Failed to start recording. Please check microphone permissions.');
      }
    }
  }

  cancelTranscription() {
    this.whisperService.stopRecording();
    this.showTranscriptionCancel = false;
    this.isConnecting = false; // Clear connecting state when cancelling
    if (this.transcriptionTimeoutId) {
      clearTimeout(this.transcriptionTimeoutId);
      this.transcriptionTimeoutId = null;
    }
    this.toastService.showSuccess('Transcription cancelled');
  }

  async startRecording() {
    try {
      await this.whisperService.startRecording();
      this.whisperService.clearTranscription(); // Clear previous transcription
      // The connecting state will be cleared in toggleMicrophone
    } catch (error) {
      // Error handling will be done in toggleMicrophone
      throw error;
    }
  }

  stopRecording() {
    this.whisperService.stopRecording();
    this.isConnecting = false; // Clear connecting state when stopping
  }

  async loadExistingThreads() {
    const agencyId = this.resolveAgencyId();
    if (!agencyId) return;

    try {
      this.threadsListSubscription?.unsubscribe();

      // Load agency-scoped threads and sort client-side by most recent first.
      this.threadsListSubscription = this.afs.collection('threads', ref => 
        ref.where('agencyId', '==', agencyId)
           .limit(20)
      ).valueChanges({ idField: 'id' }).subscribe((threads: any[]) => {
        this.availableThreads = [...threads]
          .map(thread => this.normalizeThread(thread))
          .sort((left, right) => {
            const leftTime = left?.updatedAt?.toDate ? left.updatedAt.toDate().getTime() : 0;
            const rightTime = right?.updatedAt?.toDate ? right.updatedAt.toDate().getTime() : 0;
            return rightTime - leftTime;
          });
      });

    } catch (error) {
      console.error('Error loading threads:', error);
    }
  }

  /**
   * Load a specific thread by ID and automatically select it
   */
  async loadSpecificThread(threadId: string) {
    if (!threadId) return;

    if (this.loadingSpecificThreadId === threadId && this.loadingSpecificThreadPromise) {
      await this.loadingSpecificThreadPromise;
      return;
    }

    if (this.currentThreadId !== threadId || !this.chatHistoryLoaded) {
      this.isLoadingThread = true;
      this.history = [];
      this.chatHistoryLoaded = false;
      this.initialScrollPending = true;
    }

    const loadPromise = this.loadSpecificThreadInternal(threadId);
    this.loadingSpecificThreadId = threadId;
    this.loadingSpecificThreadPromise = loadPromise.finally(() => {
      if (this.loadingSpecificThreadId === threadId) {
        this.loadingSpecificThreadId = null;
        this.loadingSpecificThreadPromise = null;
      }

      if (this.currentThreadId !== threadId) {
        this.isLoadingThread = false;
      }
    });

    await this.loadingSpecificThreadPromise;
  }

  private async loadSpecificThreadInternal(threadId: string): Promise<void> {
    if (!threadId) return;

    try {
      // Fetch the specific thread from Firestore
      const threadDoc = await this.fetchThreadDocumentWithRetry(threadId);
      
      if (threadDoc && threadDoc.exists) {
        const threadData = threadDoc.data() as any;
        const thread = this.normalizeThread(threadData, threadDoc.id);
        
        // Update the component's agent to match the thread's agentType
        const threadAgent = this.getThreadAgent(thread);
        if (threadAgent && threadAgent !== this.agent) {
          this.agent = threadAgent as any;
        }
        
        // Add this thread to availableThreads if it's not already there
        const existingThreadIndex = this.availableThreads.findIndex(t => t.id === threadId);
        if (existingThreadIndex === -1) {
          this.availableThreads.unshift(thread);
        } else {
          // Update existing thread data
          this.availableThreads[existingThreadIndex] = thread;
        }
        
        // Automatically select this thread
        this.currentThreadId = threadId;
        this.history = [];
        this.chatHistoryLoaded = false;
        this.hydrateMediaPreferencesFromThread(thread);
        this.hydrateToolSelectionFromThread(thread);
        
        // Set the selected model based on the thread's model or last used model
        this.selectedModel = this.findAvailableModel(this.getThreadPreferredModelKey(thread))
          || this.findAvailableModel(thread.lastUsedModel);
        
        // Restore mode from thread if set
        if (thread.mode === 'ask' || thread.mode === 'agent') {
          this.mode = thread.mode;
        }
        
        // Restore attached context from thread if present
        if (thread.attachedContext?.length) {
          for (const ctx of thread.attachedContext) {
            this.attachedContextService.addItem({
              id: ctx.id,
              type: ctx.type,
              displayName: ctx.displayName,
              icon: ctx.icon || undefined,
              parentId: ctx.parentId || undefined,
              parentType: ctx.parentType || undefined,
              url: ctx.url || undefined,
              mediaType: ctx.mediaType || undefined
            });
          }
        }
        
        // Reload context items for the thread's UID
        this.loadPlansAndNotes();
        
        // Set up real-time listener for the selected thread
        this.setupChatListener();
        this.applyPendingThreadDraft(threadId);
        this.specificThreadAuthRetryCounts.delete(threadId);
        
        console.log('Loaded and selected thread:', threadId);
      } else {
        console.warn('Thread not found:', threadId);
        // Thread doesn't exist yet - only attempt to create if we have a user context
        if (this.resolveAgencyId()) {
          await this.createThreadWithId(threadId);
        } else {
          // No user context yet, wait for auth then retry
          console.log('Waiting for auth before creating thread:', threadId);
          this.scheduleSpecificThreadRetry(threadId);
        }
      }
    } catch (error) {
      console.error('Error loading specific thread:', error);
      // Fall back to creating a new thread only if we have user context
      if (this.resolveAgencyId()) {
        await this.createThreadWithId(threadId);
      } else {
        this.scheduleSpecificThreadRetry(threadId);
      }
    }
  }

  private scheduleSpecificThreadRetry(threadId: string): void {
    const retryCount = this.specificThreadAuthRetryCounts.get(threadId) || 0;
    if (retryCount >= this.maxSpecificThreadAuthRetries) {
      console.warn('Stopped waiting for auth before loading thread:', threadId);
      this.isLoadingThread = false;
      return;
    }

    this.specificThreadAuthRetryCounts.set(threadId, retryCount + 1);

    setTimeout(() => {
      if (this.threadId === threadId && this.currentThreadId !== threadId) {
        void this.loadSpecificThread(threadId);
      }
    }, 300);
  }

  /**
   * Create a new thread with a specific ID
   */
  async createThreadWithId(threadId: string) {
    try {
      const threadData = this.buildThreadData();

      // Set the document with the specific ID
      await this.afs.doc(`threads/${threadId}`).set(threadData);
      
      // Add to available threads
      const thread = this.normalizeThread(threadData, threadId);
      this.availableThreads.unshift(thread);
      
      // Select this thread
      this.currentThreadId = threadId;
      this.history = [];
      this.chatHistoryLoaded = false;
      
      // Set up real-time listener
      this.setupChatListener();
      this.applyPendingThreadDraft(threadId);
      this.specificThreadAuthRetryCounts.delete(threadId);
      
      console.log('Created and selected new thread with ID:', threadId);

    } catch (error) {
      console.error('Error creating thread with specific ID:', error);
      this.toastService.showError('Failed to initialize chat thread');
    }
  }

  private async fetchThreadDocumentWithRetry(threadId: string, attempts = 10, retryDelayMs = 250): Promise<any> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      const threadDoc = await this.afs.doc(`threads/${threadId}`).get().toPromise();
      if (threadDoc?.exists) {
        return threadDoc;
      }

      if (attempt < attempts - 1) {
        await new Promise<void>(resolve => {
          setTimeout(() => resolve(), retryDelayMs);
        });
      }
    }

    return null;
  }

  selectThread(threadId: string, event?: Event) {
    // If clicking on action buttons (edit/delete), don't toggle the thread selection
    if (event && event.target) {
      const target = event.target as HTMLElement;
      const isActionButton = target.closest('.thread-action-btn') || target.closest('.thread-actions');
      if (isActionButton) {
        return;
      }
    }
    
    if (this.currentThreadId === threadId) {
      // Toggle off if the same thread is clicked
      this.clearSelectedThread();
    } else {
      // Select the new thread
      this.currentThreadId = threadId;
      this.history = [];
      this.chatHistoryLoaded = false;
      this.initialScrollPending = true;
      
      // Update the component's agent to match the thread's configured agent
      const selectedThread = this.availableThreads.find(t => t.id === threadId);
      const threadAgent = this.getThreadAgent(selectedThread);
      if (selectedThread && threadAgent && threadAgent !== this.agent) {
        this.agent = threadAgent as any;
      }
      this.hydrateMediaPreferencesFromThread(selectedThread);
      this.hydrateToolSelectionFromThread(selectedThread);
      
      // Set the selected model based on the thread's last used model
      this.selectedModel = this.findAvailableModel(this.getThreadPreferredModelKey(selectedThread))
        || this.findAvailableModel(selectedThread?.lastUsedModel)
        || this.findAvailableModel(selectedThread?.model);
      
      // Reload context items for the selected thread's UID
      this.loadPlansAndNotes();
      
      // Set up real-time listener for the selected thread
      this.setupChatListener();
      
      // Close options panel when a thread is selected
      this.showOptions = false;
      
      // Scroll to bottom after a delay to allow messages to load
      setTimeout(() => {
        this.scrollToBottom(true);
        this.initialScrollPending = false;
      }, 1000);
    }
  }

  createNewThread() {
    this.currentThreadId = null;
    this.history = [];
    this.chatHistoryLoaded = false;
    this.loading = false; // Reset loading state
    this.initializingThread = false; // Reset thread initialization state
    this.isExpanded = false; // Reset expanded state for new thread
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
      this.chatSubscription = null;
    }
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
      this.threadSubscription = null;
    }
    this.hasManualToolSelection = false;
    this.toolSelectionMode = 'all';
    this.pendingPlaybookId = null;
    this.pendingSystemPromptOverride = null;
    this.maybeApplyAgentToolDefaults();
  }

  private refreshDefaultToolNames(): void {
    this.defaultToolNames = [];
  }

  private maybeApplyAgentToolDefaults(): void {
    this.refreshDefaultToolNames();
  }

  private applyPlaybookExecutionDefaults(playbook: PlaybookEntry): void {
    const recommendedModelKey = this.normalizePreferenceString(playbook.recommendedModel);
    if (recommendedModelKey) {
      this.selectedModel = this.findAvailableModel(recommendedModelKey)
        || this.createModelPlaceholder(recommendedModelKey);
    }

    const recommendedToolGroupKeys = Array.isArray(playbook.recommendedToolGroups)
      ? playbook.recommendedToolGroups
          .map(groupKey => typeof groupKey === 'string' ? groupKey.trim() : '')
          .filter(Boolean)
      : [];

    this.pendingPlaybookToolGroupKeys = recommendedToolGroupKeys;
    this.selectedToolNames = this.resolvePlaybookToolNames(recommendedToolGroupKeys, this.availableTools);
    if (this.selectedToolNames.length > 0) {
      this.pendingPlaybookToolGroupKeys = [];
    }

    this.refreshDefaultToolNames();
    this.toolSelectionMode = inferToolSelectionMode(this.selectedToolNames, this.defaultToolNames);
    this.hasManualToolSelection = false;
    this.cdr.markForCheck();
  }

  private resolvePlaybookToolNames(groupKeys: string[], tools: ToolOption[]): string[] {
    return getToolNamesForGroups(groupKeys, tools);
  }

  private createModelPlaceholder(modelKey: string): ModelOption {
    return {
      key: modelKey,
      displayName: formatModelDisplayName(modelKey),
      category: this.getModelProvider(modelKey).toUpperCase(),
      provider: this.getModelProvider(modelKey)
    };
  }

  async initializeThread() {
    // Prevent duplicate thread creation if one is already being created
    if (this.currentThreadId) return;

    try {
      const threadData = this.buildThreadData();

      const threadRef = await this.afs.collection('threads').add(threadData);
      this.currentThreadId = threadRef.id;
      void this.persistAttachedContext();

      // Set up real-time listener for chat messages
      this.setupChatListener();

      // Close options panel after creating new thread
      this.showOptions = false;

    } catch (error) {
      console.error('Error creating thread:', error);
      this.toastService.showError('Failed to initialize chat');
    }
  }

  setupChatListener() {
    if (!this.currentThreadId) return;

    this.isLoadingThread = true;
    this.chatHistoryLoaded = false;

    // Clean up existing subscriptions
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
    }
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
    }

    // Listen for changes to the thread document itself (status, progress, etc.)
    this.threadSubscription = this.afs.doc(`threads/${this.currentThreadId}`)
      .valueChanges({ idField: 'id' }).subscribe((threadData: any) => {
        if (threadData) {
          const normalizedThread = this.normalizeThread(threadData);
          const isActiveThreadStatus = normalizedThread.status === 'running' || normalizedThread.status === 'continuing';
          const isTerminalThreadStatus = this.terminalThreadStatuses.has(normalizedThread.status);
          // Update the thread in availableThreads array
          const threadIndex = this.availableThreads.findIndex(t => t.id === normalizedThread.id);
          if (threadIndex !== -1) {
            this.availableThreads[threadIndex] = normalizedThread;
          } else {
            // Add thread if not found (shouldn't happen but just in case)
            this.availableThreads.unshift(normalizedThread);
          }
          // Invalidate the cached currentThread reference
          this._cachedThread = normalizedThread;
          this._cachedThreadId = normalizedThread.id;
          this._cachedThreadsLength = this.availableThreads.length;

          if (isActiveThreadStatus) {
            this.loading = true;
            this.sendRequestInFlight = false;
          } else if (isTerminalThreadStatus && (!this.sendRequestInFlight || normalizedThread.status !== 'waiting_for_user')) {
            this.loading = false;
            this.sendRequestInFlight = false;
          }

          console.log('Thread updated:', normalizedThread);
        } else {
          this.loading = false;
          this.sendRequestInFlight = false;
        }
      });

    // Listen for real-time chat messages, ordered by timestamp
    this.chatSubscription = this.afs.collection(`threads/${this.currentThreadId}/chat`, ref => 
      ref.orderBy('createdAt', 'asc')
    ).valueChanges({ idField: 'id' }).subscribe((messages: any[]) => {
      this.chatHistoryLoaded = true;
      this.isLoadingThread = false;

      // Skip update if messages haven't actually changed (Firestore re-emits on metadata changes)
      if (this.history.length === messages.length &&
          this.history.length > 0) {
        const messagesUnchanged = this.history.every((msg, i) => this.areMessageSnapshotsEqual(msg, messages[i]));

        if (messagesUnchanged) {
          return;
        }
      }

      // Include all messages including system and tool messages
      const allMessages = messages;
      
      // Check if we have a new assistant message compared to current history
      const newAssistantMessage = allMessages.find(msg => 
        msg.role === 'assistant' && 
        !this.history.some(histMsg => 
          (histMsg.role === 'model' || histMsg.role === 'assistant') && 
          histMsg.id === msg.id // Use ID instead of content comparison for reliability
        )
      );
      
      // If we have a new assistant message and we're currently loading, turn off loading
      if (newAssistantMessage && this.loading) {
        this.loading = false;
      }
      
      // Pass raw messages directly to the component - no transformation needed
      this.history = allMessages;

      // Scroll to bottom after DOM updates to ensure all messages are visible
      setTimeout(() => {
        // Use instant scroll only on initial load, smooth for subsequent updates
        const useInstant = this.initialScrollPending;
        this.scrollToBottom(useInstant);
        this.initialScrollPending = false;
      }, 100);
    });
  }

  async sendMessage() {
    if (this.loading || this.initializingThread || this.sendRequestInFlight) {
      return;
    }

    if (!this.newMessage?.trim() && !this.hasReadyImageAttachments && !this.hasReadyFileAttachments && this.attachedContextItems.length === 0) {
      return;
    }

    if (this.hasPendingImageUploads || this.hasPendingFileUploads) {
      this.toastService.showError('Wait for attachments to finish uploading before sending.');
      return;
    }

    if (this.hasFailedImageUploads || this.hasFailedFileUploads) {
      this.toastService.showError('Remove failed attachments before sending.');
      return;
    }

    if (this.hasReadyImageAttachments && !this.selectedModelSupportsImages) {
      this.toastService.showError('The selected model does not support image input. Choose a multimodal model first.');
      return;
    }

    if (this.hasNoAgencyCredits) {
      this.openAddCreditsModal();
      return;
    }

    this.sendRequestInFlight = true;
    try {
      // Only create a new thread if none is selected
      if (!this.currentThreadId) {
        this.initializingThread = true; // Set immediate feedback
        try {
          await this.initializeThread();
        } catch (error) {
          this.initializingThread = false;
          console.error('Error initializing thread:', error);
          this.toastService.showError('Failed to initialize chat thread');
          return;
        }
        this.initializingThread = false; // Clear after thread is created
      }

      if (!this.currentThreadId) {
        this.toastService.showError('Failed to initialize chat thread');
        return;
      }

      this.loading = true;
      const userMessage = this.newMessage.trim();
      const readyImageItems = this.inputContainer?.getReadyImageAttachments()
        || this.attachedImageItems.filter(item => item.status === 'ready' && !!item.url);
      const imageAttachments = readyImageItems
        .map<AgentImageAttachment>(item => ({
          id: item.id,
          url: item.url,
          mimeType: item.mimeType,
          ...(item.name ? { name: item.name } : {}),
          ...(typeof item.sizeBytes === 'number' ? { sizeBytes: item.sizeBytes } : {}),
          ...(typeof item.width === 'number' ? { width: item.width } : {}),
          ...(typeof item.height === 'number' ? { height: item.height } : {})
        }));
      const fileAttachments = this.inputContainer?.getReadyFileAttachments()
        || this.attachedFileItems.filter(item => item.status === 'ready' && !!item.url);
      const requestMessage = this.buildOutboundUserMessage(userMessage);
      const contextAttachments = this.buildOutboundContextAttachments(fileAttachments);
      console.log('[chat-attachments] preparing outbound request', {
        imageAttachmentCount: imageAttachments.length,
        contextAttachmentCount: contextAttachments.length,
        hasUserMessage: !!userMessage,
        requestMessagePreview: requestMessage.slice(0, 500),
        attachedContextPreview: contextAttachments.slice(0, 5).map(item => ({
          type: item.type,
          displayName: item.displayName,
          entityId: item.entityId
        }))
      });
      this.newMessage = '';

      // Reset to input mode after sending message
      this.isExpanded = false;

      // Scroll to bottom immediately after sending message to show immediate feedback
      setTimeout(() => {
        this.scrollToBottom();
      }, 0);

      const canResume =
        this.history.length > 0 &&
        (this.currentThread?.status === 'waiting_for_user' || this.currentThread?.status === 'stopped');
      const endpoint = canResume ? 'resume' : 'start';
      const playbookId = this.normalizePreferenceString(this.pendingPlaybookId);

      const requestBody: any = {
        threadId: this.currentThreadId,
        platformEnvironment: this.backendEnvironment,
        agentName: this.agent,
        userMessage: requestMessage,
        mode: this.mode,
        systemPromptLayers: this.buildSystemPromptLayersForRequest()
      };

      if (playbookId) {
        requestBody.playbookId = playbookId;
      }

      if (imageAttachments.length > 0) {
        requestBody.imageAttachments = imageAttachments;
      }

      if (contextAttachments.length > 0) {
        requestBody.contextAttachments = contextAttachments;
      }

      this.pendingSystemPromptOverride = null;
      this.pendingPlaybookId = null;

      if (this.mode === 'agent' && this.maxIterations > 0) {
        requestBody.maxIterations = this.maxIterations;
      }

      requestBody.toolFilter = this.buildToolFilterForRequest();

      // Only include model if explicitly set
      if (this.selectedModel) {
        requestBody.model = this.selectedModel.key;
      }

      const responsePreferences = this.buildResponsePreferencesForRequest();
      if (responsePreferences) {
        requestBody.responsePreferences = responsePreferences;
      }

      const response = await this.http.post(`${this.backendUrl}/agent/${endpoint}`, requestBody).toPromise();

      console.log('Backend response:', response);
      this.inputContainer?.clearAttachments();
      this.clearAttachedImages();
      this.clearAttachedFiles();

    } catch (error) {
      console.error('Error sending message:', error);
      const backendLabel = this.agentBackendTargetService.getTarget(this.useLocalBackend).label.toLowerCase();
      this.toastService.showError(`Failed to send to ${backendLabel}`);
      // Only turn off loading on error - success case is handled by the real-time listener
      this.loading = false;
    } finally {
      this.sendRequestInFlight = false;
    }
    // Removed finally block - loading is turned off by the chat listener when assistant responds
  }

  toggleExpand(newExpandedState: boolean): void {
    this.isExpanded = newExpandedState;
    // The input container component handles focusing automatically
  }

  onTextareaKeyDown(event: KeyboardEvent): void {
    if (this.handleOptionShiftComposerShortcut(event)) {
      return;
    }

    if (event.key === 'Escape') {
      this.closePopovers();
      return;
    }

    if (event.key === 'Enter' && this.hasCommandPopoverOpen) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
      return;
    }

    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private scrollToBottom(instant: boolean = false): void {
    try {
      // Use the scrollable content container for scrolling
      if (this.scrollableContent && this.scrollableContent.nativeElement) {
        const element = this.scrollableContent.nativeElement;
        if (instant) {
          element.scrollTop = element.scrollHeight;
        } else {
          element.scrollTo({
            top: element.scrollHeight,
            behavior: 'smooth'
          });
        }
      }
    } catch (error) {
      console.error('Error scrolling to bottom:', error);
    }
  }

  // Load prompts for the current agent
  // Load plans and notes for the current agency
  async loadPlansAndNotes(): Promise<void> {
    try {
      // Tear down previous context item subscriptions
      this.contextItemSubscriptions.forEach(s => s.unsubscribe());
      this.contextItemSubscriptions = [];

      this.availablePlans = [];
      this.availableNotes = [];
      this.availableProposals = [];
      this.availableTasks = [];
      this.availableTickets = [];
      this.availableApps = [];
      this.availableMedia = [];
      this.contextItems = [];

      // Use explicit context first, otherwise fall back to the agency-scoped thread context.
      const uid = this.contextUid || this.currentThread?.agencyId || this.currentThread?.uid || this.resolveAgencyId();
      
      // If no UID available, silently return without loading
      if (!uid) {
        console.warn('No UID available for loading context items');
        return;
      }

      // Load plans (wireframes)
      const plansRef = this.afs.collection(`users/${uid}/wireframes`);
      this.contextItemSubscriptions.push(plansRef.valueChanges({ idField: 'id' }).subscribe((plans: any[]) => {
        this.availablePlans = plans.map(plan => ({
          ...plan,
          type: 'plan',
          displayName: plan.name || 'Untitled Plan'
        }));
        this.updateContextItems();
      }));

      // Load notes
      const notesRef = this.afs.collection(`users/${uid}/records`);
      this.contextItemSubscriptions.push(notesRef.valueChanges({ idField: 'id' }).subscribe((notes: any[]) => {
        this.availableNotes = notes.map(note => ({
          ...note,
          type: 'note',
          displayName: note.name || note.title || 'Untitled Note'
        }));
        this.updateContextItems();
      }));

      // Load proposals
      const proposalsRef = this.afs.collection(`users/${uid}/proposals`);
      this.contextItemSubscriptions.push(proposalsRef.valueChanges({ idField: 'id' }).subscribe((proposals: any[]) => {
        this.availableProposals = proposals.map(proposal => ({
          ...proposal,
          type: 'proposal',
          displayName: proposal.name || proposal.title || 'Untitled Proposal'
        }));
        this.updateContextItems();
      }));

      // Load tasks
      const tasksRef = this.afs.collection(`users/${uid}/tasks`);
      this.contextItemSubscriptions.push(tasksRef.valueChanges({ idField: 'id' }).subscribe((tasks: any[]) => {
        this.availableTasks = tasks.map(task => ({
          ...task,
          type: 'task',
          displayName: task.name || task.title || 'Untitled Task'
        }));
        this.updateContextItems();
      }));

      // Load tickets
      const ticketsRef = this.afs.collection(`users/${uid}/tickets`);
      this.contextItemSubscriptions.push(ticketsRef.valueChanges({ idField: 'id' }).subscribe((tickets: any[]) => {
        this.availableTickets = tickets.map(ticket => ({
          ...ticket,
          type: 'ticket',
          displayName: ticket.name || ticket.title || ticket.subject || 'Untitled Ticket'
        }));
        this.updateContextItems();
      }));

      // Load apps (from builds)
      const appsRef = this.afs.collection(`users/${uid}/apps`);
      this.contextItemSubscriptions.push(appsRef.valueChanges({ idField: 'id' }).subscribe((apps: any[]) => {
        this.availableApps = apps.map(app => ({
          ...app,
          type: 'app',
          displayName: app.name || app.title || 'Untitled App'
        }));
        this.updateContextItems();
      }));

      // Load media (from support folder)
      const mediaRef = this.afs.collection(`media`, ref => 
        ref.where('uid', '==', uid)
      );
      this.contextItemSubscriptions.push(mediaRef.valueChanges({ idField: 'id' }).subscribe((media: any[]) => {
        this.availableMedia = media.map(file => ({
          ...file,
          type: 'media',
          displayName: file.displayName || file.name || 'Untitled File',
          url: file.url || file.downloadURL,
          mediaType: file.type || file.mimeType
        }));
        this.updateContextItems();
      }));
    } catch (error) {
      console.error('Error loading context items:', error);
    }
  }

  // Update the combined context items list
  private updateContextItems(): void {
    const typeOrder = { 'plan': 1, 'note': 2, 'proposal': 3, 'task': 4, 'ticket': 5, 'app': 6, 'media': 7 };
    
    this.contextItems = [
      ...this.availablePlans,
      ...this.availableNotes,
      ...this.availableProposals,
      ...this.availableTasks,
      ...this.availableTickets,
      ...this.availableApps,
      ...this.availableMedia
    ].sort((a, b) => {
      // Sort by type first (using order), then by name
      if (a.type !== b.type) {
        return (typeOrder[a.type as keyof typeof typeOrder] || 99) - (typeOrder[b.type as keyof typeof typeOrder] || 99);
      }
      return a.displayName.localeCompare(b.displayName);
    });
  }

  // Handle business selected from context popover drill-down
  onBusinessSelectedForContext(event: { uid: string; name: string }): void {
    this.contextUid = event.uid;
    this.contextBusinessName = event.name;
    this.loadPlansAndNotes();
  }

  onBusinessAttachedForContext(event: { uid: string; name: string }): void {
    this.attachedContextService.addBusiness(event.uid, event.name);
  }

  // Handle input changes for slash command detection
  onInputChange(inputEvent: InputChangeEvent): void {
    const value = inputEvent.value;
    const target = inputEvent.event.target as HTMLInputElement | HTMLTextAreaElement;
    const cursorPosition = target.selectionStart || 0;
    
    // Slash is handled inside the composer as the agency skills trigger.
    if (value === '/' || (value[cursorPosition - 1] === '/' && (cursorPosition === 1 || value[cursorPosition - 2] === ' '))) {
      this.showPromptPopover = false;
    }
    // Check if user typed @ at the beginning or after a space
    else if (value === '@' || (value[cursorPosition - 1] === '@' && (cursorPosition === 1 || value[cursorPosition - 2] === ' '))) {
      this.commandPopoverSource = 'keyboard';
      this.showAgentSelectorPopover = true;
      this.showPromptPopover = false;
      this.showContextPopover = false;
      this.showModePopover = false;
      this.showThreadDetailsPopover = false;
      this.agentCursorPosition = cursorPosition;
      // Set selection to current agent instead of first item
      this.selectedAgentIndex = this.availableAgents.findIndex(agent => agent.value === this.agent);
      if (this.selectedAgentIndex === -1) this.selectedAgentIndex = 0; // Fallback to first item if not found
      // Scroll the current agent into view after DOM update
      setTimeout(() => this.scrollSelectedAgentIntoView(), 0);
      this.openAgentSelectorPopover();
    }
    // Check if user typed # at the beginning or after a space
    else if (value === '#' || (value[cursorPosition - 1] === '#' && (cursorPosition === 1 || value[cursorPosition - 2] === ' '))) {
      this.commandPopoverSource = 'keyboard';
      this.showContextPopover = true;
      this.showPromptPopover = false;
      this.showAgentSelectorPopover = false;
      this.showModePopover = false;
      this.showThreadDetailsPopover = false;
      this.contextCursorPosition = cursorPosition;
      this.selectedContextIndex = 0; // Reset selection to first item
      this.openContextPopover();
    }
    // Check if user typed + at the beginning or after a space
    else if (value === '+' || (value[cursorPosition - 1] === '+' && (cursorPosition === 1 || value[cursorPosition - 2] === ' '))) {
      this.commandPopoverSource = 'keyboard';
      this.showModePopover = true;
      this.showPromptPopover = false;
      this.showAgentSelectorPopover = false;
      this.showContextPopover = false;
      this.showThreadDetailsPopover = false;
      this.modeCursorPosition = cursorPosition;
      this.selectedModeIndex = this.mode === 'ask' ? 0 : 1; // Set selection based on current mode
      this.openModePopover();
    } 
    // Check if user typed ? at the beginning or after a space
    else if (value === '?' || (value[cursorPosition - 1] === '?' && (cursorPosition === 1 || value[cursorPosition - 2] === ' '))) {
      this.commandPopoverSource = 'keyboard';
      this.showThreadDetailsPopover = true;
      this.showPromptPopover = false;
      this.showAgentSelectorPopover = false;
      this.showContextPopover = false;
      this.showModePopover = false;
      this.threadDetailsCursorPosition = cursorPosition;
      this.openThreadDetailsPopover();
    } else {
      this.closePopovers();
    }
  }

  // Handle key events in the input
  onInputKeyDown(event: KeyboardEvent): void {
    if (this.handleOptionShiftComposerShortcut(event)) {
      return;
    }

    // Handle normal Enter key for sending messages when no popover is active
    if (event.key === 'Enter' && !this.showPromptPopover && !this.showAgentSelectorPopover && 
        !this.showContextPopover && !this.showModePopover && !this.showThreadDetailsPopover) {
      this.sendMessage();
    }
    // Handle escape key for any popover
    else if (event.key === 'Escape') {
      this.closePopovers();
    }
  }

  // Handle playbook submission from the popover
  onPlaybookSubmitted(submission: PlaybookSubmission): void {
    this.applyPlaybookExecutionDefaults(submission.playbook);
    this.newMessage = submission.resolvedPrompt;
    this.pendingSystemPromptOverride = submission.playbook.systemPromptOverride || null;
    this.pendingPlaybookId = submission.playbook.slug || null;
    this.closePopovers();
    
    // Auto-send the message
    this.sendMessage();
  }

  // Scroll the selected prompt into view within the popover
  private scrollSelectedPromptIntoView(): void {
    setTimeout(() => {
      const selectedElement = document.querySelector('.prompt-item.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, 0);
  }

  // Scroll the selected agent into view within the popover
  private scrollSelectedAgentIntoView(): void {
    setTimeout(() => {
      const selectedElement = document.querySelector('.agent-selector-item.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, 0);
  }

  // Scroll the selected context item into view within the popover
  private scrollSelectedContextIntoView(): void {
    setTimeout(() => {
      const selectedElement = document.querySelector('.context-item.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, 0);
  }

  // Select a context item from the popover and replace # symbol
  selectContextItem(event: any): void {
    const item = event.item || event; // Support both new format {item, keepOpen} and old format (direct item)
    const keepOpen = event.keepOpen || false;
    const parentId = item.parentId || this.contextUid || undefined;
    const parentType = item.parentType || (parentId ? 'business' : undefined);
    const parentDisplayName = item.parentDisplayName || this.contextBusinessName || undefined;

    // Add item to attached context based on type
    switch (item.type) {
      case 'business':
        this.attachedContextService.addBusiness(item.id, item.displayName);
        break;
      case 'partner':
        this.attachedContextService.addPartner(item.id, item.displayName);
        break;
      case 'funnel':
        this.attachedContextService.addFunnel(item.id, item.displayName);
        break;
      case 'page':
        this.attachedContextService.addPage(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'form':
        this.attachedContextService.addForm(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'note':
        this.attachedContextService.addNote(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'plan':
        this.attachedContextService.addPlan(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'proposal':
        this.attachedContextService.addProposal(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'task':
        this.attachedContextService.addTask(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'ticket':
        this.attachedContextService.addTicket(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'app':
        this.attachedContextService.addApp(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'media':
        this.attachedContextService.addMedia(item.id, item.displayName, item.url, item.mediaType, parentId, parentType, parentDisplayName);
        break;
    }

    if (parentId && parentType && parentDisplayName) {
      switch (parentType) {
        case 'business':
          this.attachedContextService.addBusiness(parentId, parentDisplayName);
          break;
        case 'funnel':
          this.attachedContextService.addFunnel(parentId, parentDisplayName);
          break;
      }
    }
    
    // Remove the # symbol from the input (only if closing)
    if (!keepOpen) {
      this.newMessage = this.stripCommandTrigger(this.contextCursorPosition, '#');
      
        this.closePopovers();
      
      // Focus back to input
      setTimeout(() => {
        if (this.inputContainer) {
          this.inputContainer.focusInput();
        }
      }, 0);
    }
    // If keeping open, don't remove # and don't close popover
  }

  // Select an agent from the popover and replace @ symbol
  selectAgentFromPopover(agent: AgentOption): void {
    this.newMessage = this.stripCommandTrigger(this.agentCursorPosition, '@');
    this.closePopovers();
    
    // Change the actual agent
    this.selectAgent(agent.value);
    
    // Focus back to input
    setTimeout(() => {
      if (this.inputContainer) {
        this.inputContainer.focusInput();
      }
    }, 0);
  }

  // Format thread date for display
  formatThreadDate(timestamp: any): string {
    if (!timestamp) return '';
    
    // Handle Firebase Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return minutes <= 1 ? 'Just now' : `${minutes}m ago`;
    }
    
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    
    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}d ago`;
    }
    
    // More than 7 days - show date
    return date.toLocaleDateString();
  }

  // Enhanced settings popover methods
  selectModeFromPopover(modeOption: { value: 'ask' | 'agent', label: string, type: 'mode' }): void {
    this.newMessage = this.stripCommandTrigger(this.modeCursorPosition, '+');
    this.closePopovers();
    this.setMode(modeOption.value as 'ask' | 'agent');
    
    // Focus back to input
    setTimeout(() => {
      if (this.inputContainer) {
        this.inputContainer.focusInput();
      }
    }, 0);
  }

  selectModelFromPopover(model: ModelOption): void {
    this.newMessage = this.stripCommandTrigger(this.modeCursorPosition, '+');
    this.selectedModel = model;
    this.closePopovers();
    
    // Focus back to input
    setTimeout(() => {
      if (this.inputContainer) {
        this.inputContainer.focusInput();
      }
    }, 0);
  }

  // Handle removing an attached context item
  onRemoveAttachedContext(item: AttachedContextItem): void {
    this.attachedContextService.removeItem(item);
  }

  onImageFilesPasted(files: File[]): void {
    void this.attachImageFiles(files);
  }

  onImageFilesSelected(files: File[]): void {
    console.log('[chat-attachments] image files selected in chat component', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });
    void this.attachImageFiles(files);
  }

  onFilesSelected(files: File[]): void {
    console.log('[chat-attachments] generic files selected in chat component', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });
    void this.attachFiles(files);
  }

  onRemoveAttachedImage(item: ChatImageAttachment): void {
    this.removeImageAttachment(item.id);
  }

  onRemoveAttachedFile(item: ChatFileAttachment): void {
    this.removeFileAttachment(item.id);
  }

  private stripCommandTrigger(cursorPosition: number, trigger: string): string {
    if (this.commandPopoverSource !== 'keyboard') {
      return this.newMessage;
    }

    if (cursorPosition <= 0 || this.newMessage[cursorPosition - 1] !== trigger) {
      return this.newMessage;
    }

    return this.newMessage.substring(0, cursorPosition - 1) + this.newMessage.substring(cursorPosition);
  }

  private replaceCommandTrigger(cursorPosition: number, trigger: string, replacement: string): string {
    const shouldReplaceTrigger = this.commandPopoverSource === 'keyboard' && cursorPosition > 0 && this.newMessage[cursorPosition - 1] === trigger;
    const before = shouldReplaceTrigger
      ? this.newMessage.substring(0, cursorPosition - 1)
      : this.newMessage.substring(0, cursorPosition);
    const after = this.newMessage.substring(cursorPosition);

    return before + replacement + after;
  }

  // ============================================================
  // DRAG AND DROP FUNCTIONALITY
  // ============================================================

  private boundGlobalDragEnter: ((event: DragEvent) => void) | null = null;
  private boundGlobalDragOver: ((event: DragEvent) => void) | null = null;
  private boundGlobalDragLeave: ((event: DragEvent) => void) | null = null;
  private boundGlobalDrop: ((event: DragEvent) => void) | null = null;
  private boundGlobalDragEnd: ((event: DragEvent) => void) | null = null;

  /**
   * Set up global drag event listeners to detect dragging from anywhere
   */
  private setupGlobalDragListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Create bound functions so we can remove them later
    this.boundGlobalDragEnter = this.globalDragEnter.bind(this);
    this.boundGlobalDragOver = this.globalDragOver.bind(this);
    this.boundGlobalDragLeave = this.globalDragLeave.bind(this);
    this.boundGlobalDrop = this.globalDrop.bind(this);
    this.boundGlobalDragEnd = this.globalDragEnd.bind(this);

    // Add listeners to window
    window.addEventListener('dragenter', this.boundGlobalDragEnter);
    window.addEventListener('dragover', this.boundGlobalDragOver);
    window.addEventListener('dragleave', this.boundGlobalDragLeave);
    window.addEventListener('drop', this.boundGlobalDrop);
    window.addEventListener('dragend', this.boundGlobalDragEnd);
  }

  /**
   * Clean up global drag event listeners
   */
  private cleanupGlobalDragListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.boundGlobalDragEnter) {
      window.removeEventListener('dragenter', this.boundGlobalDragEnter);
    }
    if (this.boundGlobalDragOver) {
      window.removeEventListener('dragover', this.boundGlobalDragOver);
    }
    if (this.boundGlobalDragLeave) {
      window.removeEventListener('dragleave', this.boundGlobalDragLeave);
    }
    if (this.boundGlobalDrop) {
      window.removeEventListener('drop', this.boundGlobalDrop);
    }
    if (this.boundGlobalDragEnd) {
      window.removeEventListener('dragend', this.boundGlobalDragEnd);
    }
  }

  /**
   * Global drag enter - show overlay when dragging starts anywhere
   */
  private globalDragEnter(event: DragEvent): void {
    if (this.isSupportedImageDrag(event) || event.dataTransfer?.types.includes('application/json')) {
      this.dragCounter++;
      if (!this.isDragOver) {
        this.isDragOver = true;
      }
    }
  }

  /**
   * Global drag over - keep overlay visible and allow drop
   */
  private globalDragOver(event: DragEvent): void {
    if (this.isSupportedImageDrag(event) || event.dataTransfer?.types.includes('application/json')) {
      event.preventDefault(); // Required to allow drop
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    }
  }

  /**
   * Global drag leave - track when leaving elements
   */
  private globalDragLeave(event: DragEvent): void {
    this.dragCounter--;
    
    // Only hide when we've left all elements
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragOver = false;
    }
  }

  /**
   * Global drop - handle drop anywhere and reset state
   */
  private globalDrop(event: DragEvent): void {
    // Always reset drag state
    this.isDragOver = false;
    this.dragCounter = 0;
  }

  /**
   * Global drag end - ensure overlay is dismissed when drag operation ends
   * This catches cases where the user cancels the drag or drags outside the window
   */
  private globalDragEnd(event: DragEvent): void {
    // Always reset drag state when drag ends
    this.isDragOver = false;
    this.dragCounter = 0;
  }

  /**
   * Handle drag over event - show drop zone (for chat container)
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  /**
   * Handle drag leave event - track leaving (for chat container)
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Handle drop event - add media as context
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    this.isDragOver = false;
    this.dragCounter = 0;
    
    if (event.dataTransfer) {
      const droppedImageFiles = this.extractSupportedImageFiles(event.dataTransfer);
      if (droppedImageFiles.length > 0) {
        void this.attachImageFiles(droppedImageFiles);
        return;
      }

      const jsonData = event.dataTransfer.getData('application/json');
      
      if (jsonData) {
        try {
          const dragData = JSON.parse(jsonData);
          
          // Handle different entity types - no toast needed, visual feedback is enough
          switch (dragData.type) {
            case 'media':
              this.attachedContextService.addMedia(
                dragData.id,
                dragData.displayName,
                dragData.url,
                dragData.mediaType
              );
              break;
              
            case 'plan':
              this.attachedContextService.addPlan(
                dragData.id,
                dragData.displayName
              );
              break;
              
            case 'note':
              this.attachedContextService.addNote(
                dragData.id,
                dragData.displayName
              );
              break;
              
            case 'proposal':
              this.attachedContextService.addProposal(
                dragData.id,
                dragData.displayName
              );
              break;
              
            case 'task':
              this.attachedContextService.addTask(
                dragData.id,
                dragData.displayName
              );
              break;
              
            case 'ticket':
              this.attachedContextService.addTicket(
                dragData.id,
                dragData.displayName
              );
              break;
              
            case 'app':
              this.attachedContextService.addApp(
                dragData.id,
                dragData.displayName
              );
              break;
              
            default:
              console.warn('Unknown drag data type:', dragData.type);
              // Silently ignore unsupported types
          }
        } catch (error) {
          console.error('Error parsing drag data:', error);
          // Silently fail - don't show error toast to user
        }
      }
    }
  }

  private async attachImageFiles(files: File[]): Promise<void> {
    console.log('[chat-attachments] attachImageFiles invoked', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });

    const supportedFiles = files.filter(file => this.isSupportedImageFile(file));

    console.log('[chat-attachments] supported image files', {
      supportedCount: supportedFiles.length,
      supportedFileNames: supportedFiles.map(file => file.name)
    });

    if (supportedFiles.length === 0) {
      this.toastService.showError('Only PNG, JPEG, WebP, and GIF images can be attached.');
      return;
    }

    const remainingSlots = Math.max(0, MAX_IMAGE_ATTACHMENTS - this.attachedImageItems.length);
    if (remainingSlots === 0) {
      this.toastService.showError(`You can attach up to ${MAX_IMAGE_ATTACHMENTS} images per message.`);
      return;
    }

    const filesToAttach = supportedFiles.slice(0, remainingSlots);
    if (supportedFiles.length > filesToAttach.length) {
      this.toastService.showError(`Only the first ${remainingSlots} image${remainingSlots === 1 ? '' : 's'} were attached.`);
    }

    for (const file of filesToAttach) {
      if (file.size > MAX_IMAGE_ATTACHMENT_BYTES) {
        this.toastService.showError(`${file.name || 'Image'} is larger than 10 MB and cannot be attached.`);
        continue;
      }

      const attachment = this.createPendingImageAttachment(file);
      this.attachedImageItems = [...this.attachedImageItems, attachment];
      console.log('[chat-attachments] queued image attachment', {
        id: attachment.id,
        name: attachment.name,
        sizeBytes: attachment.sizeBytes
      });
      this.cdr.detectChanges();
      void this.uploadImageAttachment(file, attachment);
    }
  }

  private async attachFiles(files: File[]): Promise<void> {
    console.log('[chat-attachments] attachFiles invoked', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });

    const candidateFiles = files.filter(file => !!file);

    if (candidateFiles.length === 0) {
      return;
    }

    for (const file of candidateFiles) {
      if (file.size > MAX_CONTEXT_FILE_BYTES) {
        this.toastService.showError(`${file.name || 'File'} is larger than 25 MB and cannot be attached.`);
        continue;
      }

      try {
        const attachment = this.createPendingFileAttachment(file);
        this.attachedFileItems = [...this.attachedFileItems, attachment];
        console.log('[chat-attachments] queued file attachment', {
          id: attachment.id,
          name: attachment.name,
          sizeBytes: attachment.sizeBytes,
          mimeType: attachment.mimeType
        });
        this.cdr.detectChanges();
        void this.uploadFileAttachment(file, attachment);
      } catch (error) {
        console.error('Failed to upload context file attachment:', error);
        this.toastService.showError(`Failed to attach ${file.name || 'file'}.`);
      }
    }
  }

  private createPendingImageAttachment(file: File): ChatImageAttachment {
    const previewUrl = URL.createObjectURL(file);
    const inferredName = file.name?.trim() || `image-${Date.now()}`;

    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      name: inferredName,
      mimeType: file.type,
      sizeBytes: file.size,
      previewUrl,
      status: 'uploading',
      uploadProgress: 0,
      url: ''
    };
  }

  private createPendingFileAttachment(file: File): ChatFileAttachment {
    const inferredName = file.name?.trim() || `file-${Date.now()}`;

    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      name: inferredName,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      status: 'uploading',
      uploadProgress: 0,
      url: ''
    };
  }

  private async uploadImageAttachment(file: File, attachment: ChatImageAttachment): Promise<void> {
    const storagePath = this.buildImageAttachmentStoragePath(file, attachment.id);
    console.log('[chat-attachments] starting image upload', {
      id: attachment.id,
      name: attachment.name,
      storagePath
    });

    try {
      const dimensions = await this.readImageDimensions(attachment.previewUrl || '');
      const uploadTask = this.afStorage.storage.ref(storagePath).put(file, {
        contentType: file.type,
        cacheControl: 'public,max-age=31536000'
      });
      uploadTask.on('state_changed', snapshot => {
        const uploadProgress = snapshot.totalBytes > 0
          ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          : 0;
        this.updateImageAttachment(attachment.id, { uploadProgress });
      });
      const snapshot = await uploadTask;
      const downloadUrl = await snapshot.ref.getDownloadURL();

      console.log('[chat-attachments] image upload complete', {
        id: attachment.id,
        name: attachment.name,
        downloadUrl
      });

      this.updateImageAttachment(attachment.id, {
        url: downloadUrl,
        storagePath,
        status: 'ready',
        uploadProgress: 100,
        ...(dimensions.width ? { width: dimensions.width } : {}),
        ...(dimensions.height ? { height: dimensions.height } : {})
      });
    } catch (error) {
      console.error('Failed to upload chat image attachment:', error);
      this.updateImageAttachment(attachment.id, {
        status: 'error',
        uploadProgress: 0,
        errorMessage: 'Upload failed'
      });
      this.toastService.showError(`Failed to upload ${attachment.name || 'image'}.`);
    }
  }

  private async uploadFileAttachment(file: File, attachment: ChatFileAttachment): Promise<void> {
    const storagePath = this.buildContextFileStoragePath(file, attachment.id);
    console.log('[chat-attachments] starting file upload', {
      id: attachment.id,
      name: attachment.name,
      storagePath
    });

    try {
      const uploadTask = this.afStorage.storage.ref(storagePath).put(file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public,max-age=31536000'
      });
      uploadTask.on('state_changed', snapshot => {
        const uploadProgress = snapshot.totalBytes > 0
          ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          : 0;
        this.updateFileAttachment(attachment.id, { uploadProgress });
      });
      const snapshot = await uploadTask;
      const url = await snapshot.ref.getDownloadURL();

      console.log('[chat-attachments] file upload complete', {
        id: attachment.id,
        name: attachment.name,
        url
      });

      this.updateFileAttachment(attachment.id, {
        url,
        storagePath,
        status: 'ready',
        uploadProgress: 100
      });
    } catch (error) {
      console.error('Failed to upload file attachment:', error);
      this.updateFileAttachment(attachment.id, {
        status: 'error',
        uploadProgress: 0,
        errorMessage: 'Upload failed'
      });
      this.toastService.showError(`Failed to upload ${attachment.name || 'file'}.`);
    }
  }

  private updateImageAttachment(id: string, patch: Partial<ChatImageAttachment>): void {
    this.attachedImageItems = this.attachedImageItems.map(item =>
      item.id === id
        ? { ...item, ...patch }
        : item
    );
    this.cdr.detectChanges();
  }

  private updateFileAttachment(id: string, patch: Partial<ChatFileAttachment>): void {
    this.attachedFileItems = this.attachedFileItems.map(item =>
      item.id === id
        ? { ...item, ...patch }
        : item
    );
    this.cdr.detectChanges();
  }

  private removeImageAttachment(id: string): void {
    const attachment = this.attachedImageItems.find(item => item.id === id);
    if (attachment?.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.previewUrl);
    }

    this.attachedImageItems = this.attachedImageItems.filter(item => item.id !== id);
    this.cdr.detectChanges();
  }

  private removeFileAttachment(id: string): void {
    this.attachedFileItems = this.attachedFileItems.filter(item => item.id !== id);
    this.cdr.detectChanges();
  }

  private clearAttachedImages(): void {
    for (const attachment of this.attachedImageItems) {
      if (attachment.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    }

    this.attachedImageItems = [];
  }

  private clearAttachedFiles(): void {
    this.attachedFileItems = [];
  }

  private async persistAttachedContext(): Promise<void> {
    if (!this.currentThreadId) {
      return;
    }

    const attachedContext = this.attachedContextItems.map(item => ({
      id: item.id,
      type: item.type,
      displayName: item.displayName,
      ...(item.entityId ? { entityId: item.entityId } : {}),
      ...(item.icon ? { icon: item.icon } : {}),
      ...(item.filePath ? { filePath: item.filePath } : {}),
      ...(item.content ? { content: item.content } : {}),
      ...(item.parentId ? { parentId: item.parentId } : {}),
      ...(item.parentType ? { parentType: item.parentType } : {}),
      ...(item.parentDisplayName ? { parentDisplayName: item.parentDisplayName } : {}),
      ...(item.url ? { url: item.url } : {}),
      ...(item.mediaType ? { mediaType: item.mediaType } : {}),
      ...(item.mimeType ? { mimeType: item.mimeType } : {}),
      ...(typeof item.sizeBytes === 'number' ? { sizeBytes: item.sizeBytes } : {})
    }));

    try {
      await this.afs.doc(`threads/${this.currentThreadId}`).set({
        attachedContext,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('Failed to persist attached context:', error);
    }
  }

  private buildOutboundUserMessage(message: string): string {
    return message.trim();
  }

  private buildOutboundContextAttachments(files: ChatFileAttachment[]): AgentContextAttachment[] {
    const contextItems = this.attachedContextItems.map<AgentContextAttachment>(item => ({
      id: item.id,
      type: item.type,
      displayName: item.displayName,
      ...(item.entityId ? { entityId: item.entityId } : {}),
      ...(item.filePath ? { filePath: item.filePath } : {}),
      ...(item.content ? { content: item.content } : {}),
      ...(item.url ? { url: item.url } : {}),
      ...(item.mimeType ? { mimeType: item.mimeType } : {}),
      ...(item.mediaType ? { mediaType: item.mediaType } : {}),
      ...(typeof item.sizeBytes === 'number' ? { sizeBytes: item.sizeBytes } : {}),
      ...(item.icon ? { icon: item.icon } : {}),
      ...(item.parentId ? { parentId: item.parentId } : {}),
      ...(item.parentType ? { parentType: item.parentType } : {}),
      ...(item.parentDisplayName ? { parentDisplayName: item.parentDisplayName } : {})
    }));

    const fileItems = files.map<AgentContextAttachment>(file => ({
      id: file.id,
      type: 'file',
      displayName: file.name,
      url: file.url,
      mimeType: file.mimeType,
      ...(typeof file.sizeBytes === 'number' ? { sizeBytes: file.sizeBytes } : {}),
      icon: 'fa-file'
    }));

    return [...contextItems, ...fileItems];
  }

  private isSupportedImageFile(file: File): boolean {
    return !!file.type && SUPPORTED_IMAGE_MIME_TYPES.has(file.type.toLowerCase());
  }

  private extractSupportedImageFiles(dataTransfer: DataTransfer): File[] {
    return Array.from(dataTransfer.files || []).filter(file => this.isSupportedImageFile(file));
  }

  private isSupportedImageDrag(event: DragEvent): boolean {
    const types = Array.from(event.dataTransfer?.types || []);
    if (types.includes('Files')) {
      const items = Array.from(event.dataTransfer?.items || []);

      if (items.length === 0) {
        return true;
      }

      return items.some(item => item.kind === 'file' && (!item.type || item.type.toLowerCase().startsWith('image/')));
    }

    return false;
  }

  private buildImageAttachmentStoragePath(file: File, attachmentId: string): string {
    const ownerId = this.resolveAgencyId() || this.currentUser?.uid || 'anonymous';
    const threadSegment = this.currentThreadId || 'draft';
    const safeName = (file.name || 'image')
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/-+/g, '-');

    return `users/${ownerId}/agent-chat-images/${threadSegment}/${attachmentId}-${safeName}`;
  }

  private buildContextFileStoragePath(file: File, attachmentId: string): string {
    const ownerId = this.resolveAgencyId() || this.currentUser?.uid || 'anonymous';
    const threadSegment = this.currentThreadId || 'draft';
    const safeName = (file.name || 'file')
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/-+/g, '-');

    return `users/${ownerId}/agent-chat-files/${threadSegment}/${attachmentId}-${safeName}`;
  }

  private readImageDimensions(sourceUrl: string): Promise<{ width?: number; height?: number }> {
    if (!sourceUrl) {
      return Promise.resolve({});
    }

    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => resolve({});
      image.src = sourceUrl;
    });
  }
}
